/**
 * 提示词拼装层。
 * 前端负责把结构化表单拼成完整 prompt 一起提交（后端可原样使用，也可在服务端再包一层），
 * 这样做的好处：用户能在报价确认弹窗里看到「到底要发什么给模型」，出问题好定位。
 *
 * ⚠️ 这一层**不许出现硬编码的语言**。
 * 上一版在 3 处写死「画面文字使用中文」，导致投 Amazon / Ozon 也出中文图。
 * 现在语言、尺寸、卖点框架、合规禁词全部从 `config/platforms.ts` 的
 * 平台档案推导 —— 换平台 = 换一整套规则，而不是只换一行标签。
 */
import {
  AVATAR_SOURCES,
  BANNER_SIZES,
  BANNER_TYPES,
  COPY_CHANNELS,
  DIGITAL_HUMAN_MODES,
  DIGITAL_HUMAN_USES,
  FASHION_FEATURES,
  IMAGE_TOOLS,
  PRODUCT_FEATURES,
  SOCIAL_CHANNELS,
  findFeature,
} from './catalog'
import {
  PLATFORMS,
  bannedBlock,
  getPlatform,
  sellingPointBlock,
  textLanguageRule,
  type PlatformProfile,
} from './platforms'
import { DEFAULT_PRODUCT_LANGUAGE, productLanguageLabel } from './productAssets'
import { modelShotPlanText } from './modelProfile'
import { renderTemplate, type TemplateFlags, type TemplateVars } from './promptTemplates'
import { displaySize, isPixelSize, ratioHint } from './platformDetails'
import { PRODUCT_KIND_GUIDE, SCALE_NAME, describePlan, type PlannedShot } from './detailPlan'

const join = (parts: (string | undefined | null | false)[], sep = '\n') => parts.filter(Boolean).join(sep)

/**
 * 参考图保真约束 —— 只要表单里带了产品参考图就必须拼进 prompt。
 *
 * 图生图出废图最常见的原因不是模型不行，而是**提示词压根没提参考图**。
 * 模型只看到「帽子 / 美式」，就按文字自由发挥（出了藏蓝色 NY 棒球帽），
 * 用户上传的草绿色帽子被当空气 —— 这不是模型不听话，是没告诉它要听谁的话。
 *
 * 两点必须写死：
 *   1. 把「参考图 = 唯一外观依据」讲成硬约束，并逐项点名要锁的特征；
 *   2. 明确**冲突时的优先级**（图 > 文字）。不写这句，模型会在两者间瞎折中。
 * 同时明确「允许变什么」，否则模型会连角度场景都不敢动，出图僵硬。
 */
export function refFidelityBlock(productName?: string, hasRef = false): string[] {
  if (!hasRef) return []
  const subject = productName?.trim() ? `参考图中的「${productName.trim()}」` : '参考图中的产品'
  return [
    '',
    '【参考图保真要求｜最高优先级】',
    `· 本次已提供产品参考图，必须严格以 ${subject} 实物为准`,
    '· 必须保持一致：颜色与色号、材质与纹理、图案与印花、logo 与文字、五金配件、结构比例、缝线与工艺细节',
    '· 严禁替换为同品类的其他款式，严禁改变颜色，严禁自行添加或删减品牌标识、图案与文字',
    '· 允许调整：拍摄角度、场景、光线、构图、背景；不允许调整：产品本身的外观特征',
    '· 当文字描述与参考图冲突时，一律以参考图为准',
  ]
}

export type BannerInput = {
  platform: string
  type: string
  size: string
  productName: string
  mainCopy: string
  subCopy: string
  sellingPoints: string
  extra: string
  textMode: 'ai' | 'none' | 'custom'
  resolution: string
  /**
   * 自定义画面比例 —— 留空 = **跟随画布尺寸**（`BANNER_SIZES[].ratio`）。
   *
   * 2026-10-10 用户口径「这些……都可以自定义比例」：广告图原来比例只能由
   * 「画布尺寸」间接决定，想出一个不在预设画布里的比例没有入口。填了它就以它为准
   * （报价、提交、提示词三处同源，见页面里的 `effectiveRatio`）。
   */
  aspectRatio?: string
  /** 是否带了产品参考图 —— 决定要不要拼 refFidelityBlock */
  hasRef?: boolean
}

export function buildBannerPrompt(i: BannerInput): string {
  const profile = getPlatform(i.platform)
  const t = BANNER_TYPES.find((x) => x.id === i.type)
  const s = BANNER_SIZES.find((x) => x.id === i.size)
  // 自定义比例优先；没填才用画布自带的比例。界面报的必须是真正会发出去的那一份。
  const ratio = i.aspectRatio || s?.ratio
  return join([
    `任务：生成一张电商 Banner 主视觉。`,
    '',
    `【投放平台】${profile.name}（${profile.region}）`,
    `【平台风格】${profile.styleKeywords.join('、')}`,
    `【场景类型】${t?.name ?? i.type}（${t?.description ?? ''}）`,
    // 自定义比例时画布像素不再成立（那个尺寸是给预设画布用的），只报比例
    `【画布】${s?.name ?? ''} ${i.aspectRatio ? ratio : `${s?.w}×${s?.h}px（${ratio}）`}`,
    `【分辨率】${i.resolution}`,
    '',
    '【产品】',
    `· 品名：${i.productName || '（未填写）'}`,
    `· 主标题：${i.mainCopy || '（未填写，请按产品生成）'}`,
    i.subCopy ? `· 副标题：${i.subCopy}` : '',
    i.sellingPoints ? `· 卖点：${i.sellingPoints}` : '',
    i.extra ? `· 补充要求：${i.extra}` : '',
    ...refFidelityBlock(i.productName, Boolean(i.hasRef)),
    '',
    '【卖点组织方式】',
    ...sellingPointBlock(profile, i.sellingPoints),
    '',
    '【构图与视觉要求】',
    '· 产品为主视觉主体，位于画面黄金分割点，占比不低于 40%',
    '· 背景与产品形成明度对比，保证小尺寸下依然醒目',
    '· 留出文字安全区，文字不压产品主体、不被裁切',
    i.textMode === 'none'
      ? '· 画面不出现任何文字，纯视觉素材'
      : i.textMode === 'custom'
        ? `· 画面文字必须严格使用我提供的主标题/副标题，不得新增或改写任何文字；语言为 ${profile.textLanguage}`
        : textLanguageRule(profile),
    '· 商用级摄影质感，光影干净，真实产品还原，不要出现多余品牌标识与水印',
    '',
    '【合规】',
    bannedBlock(profile),
  ])
}

/**
 * 「画面文字」三档的**单一真源**。
 *
 * 三处要用同一份：表单里的 `Segmented`、第 3 页签核对清单、以及 `buildBannerFillBrief`
 * 给 AI 的前提。各写一份的后果不是排版错乱，而是**前提不一致** ——
 * AI 以为画面要出文字、用户选的是「完全无文字」，填出来的东西一个像素也不会进画面。
 */
export const TEXT_MODES = ['ai', 'custom', 'none'] as const
export const TEXT_MODE_LABEL: Record<string, string> = {
  ai: 'AI 生成文字',
  custom: '用我给的文案',
  none: '完全无文字',
}

/**
 * 「AI 填充」给后端拼的上下文（`/banner` 第 1 页签那颗按钮，一次 ¥0.10）。
 *
 * 三个刻意的选择：
 * ① **上下文在前端拼**（与分镜提示词改写同一规矩）：字段清单、平台禁词、场景与尺寸的
 *    真源都在前端，后端再拼一份就是同一份东西写两遍；
 * ② **把用户已经填的也带上**，并明确「能沿用就沿用」—— 他改到第三稿的主标题
 *    不该被一键抹掉；
 * ③ **带上「画面文字」档位**：档位是「完全无文字」时，AI 写再多文案也不会进画面，
 *    这个前提必须让它知道（前端在那一档直接不让点，见 `BannerStudio.doAiFill`）。
 */
export function buildBannerFillBrief(i: BannerInput): string {
  const profile = getPlatform(i.platform)
  const t = BANNER_TYPES.find((x) => x.id === i.type)
  const s = BANNER_SIZES.find((x) => x.id === i.size)
  const ratio = i.aspectRatio || s?.ratio
  return join([
    '这是一张电商 Banner 的上下文。产品是确定的，请把图上要出现的文字补齐。',
    '',
    `【产品】${i.productName || '（未填写）'}`,
    `【投放平台】${profile.name}（${profile.region}）`,
    `【图内文字语言】必须是：${profile.textLanguage}`,
    `【平台风格】${profile.styleKeywords.join('、')}`,
    `【场景类型】${t?.name ?? i.type}（${t?.description ?? ''}）`,
    `【画布】${s?.name ?? ''} ${i.aspectRatio ? ratio : `${s?.w}×${s?.h}px（${ratio}）`} —— 主标题要短到在这个尺寸下可读`,
    `【产品参考图】${
      i.hasRef
        ? '有（产品外观以参考图为准，文案不必再描述外观细节）'
        : '没有（只能靠文字描述产品）'
    }`,
    `【画面文字档位】${TEXT_MODE_LABEL[i.textMode] ?? i.textMode}`,
    '',
    '【用户已经填的 —— 能沿用就沿用，只在明显与上下文矛盾时才改】',
    `· 主标题：${i.mainCopy || '（空）'}`,
    `· 副标题：${i.subCopy || '（空）'}`,
    `· 卖点 / 促销信息：${i.sellingPoints || '（空）'}`,
    `· 补充要求：${i.extra || '（空）'}`,
    '',
    '【平台合规】下面这些词与同类宣称一律不许出现在你写的文案里：',
    bannedBlock(profile),
  ])
}

/* -------------------------------------------------------------------------- */
/* 「AI 填充」的上下文 —— 各页各拼一份                                         */
/* -------------------------------------------------------------------------- */

/**
 * 上面那条是 `/banner` 的。2026-10-01 用户要求「其他页面也加一个 AI 按钮」，
 * 于是按钮抽成了 `components/AiFillButton.tsx`（**全站一份实现**），
 * 但**上下文仍然各页自己拼** —— 这是刻意的，不是没抽出共性：
 *
 * · 每页「与该次填充有关的信息」本来就不一样：视频页要看场景与运镜、
 *   社媒图页要看渠道的叙事结构、文案页要看语气与篇幅。硬凑成一个通用函数的代价，
 *   是每个调用点都在传一串 `undefined`，还得在里面写 `if (page === 'video')`。
 * · 上下文里出现的每个字段都必须是**本页的真源**（平台档案 / 渠道表 / 卖点框架），
 *   各页自己取最不容易漏 —— 抽到公共文件里反而会开始出现「这里为什么写死了小红书」。
 *
 * 三条与 `buildBannerFillBrief` 完全一致的规矩（后面三个函数都照做）：
 * ① **上下文在前端拼**（字段清单、平台禁词的真源都在前端）；
 * ② **把用户已经填的也带上**，并写明「能沿用就沿用」—— 他改到第三稿的东西
 *    不该被一键抹掉；
 * ③ **带上平台合规禁词**：AI 写的卖点最后会原样进提示词，禁词在填的时候就得挡住，
 *    等到出图才发现「国家级」被写进画面就晚了。
 *
 * 三个 builder **都不含产品名以外的「AI 的产出」** —— 产品名是**输入**，
 * 所以它既在上下文里、又不在白名单里（AI 不改输入）。
 */

/**
 * 视频页「内容与场景」的填充上下文。
 *
 * 与成片参数（时长 / 比例 / 风格）有关的部分要带上：它们决定卖点该用
 * 什么口气写 —— 15 秒 TVC 要的是三行硬信息，10 秒种草片要的是能上画面的画面感。
 * 只给产品名的话，AI 写出来的东西放哪一条模板里都别扭。
 */
export function buildVideoFillBrief(
  i: Pick<VideoInput, 'productName' | 'scene' | 'sceneLabel' | 'sellingPoints' | 'duration' | 'aspectRatio' | 'style' | 'extra'> & {
    platform?: string
    templateName?: string
    refCount?: number
    /** 这条片子的镜头脚本 —— **必须给**，理由见下面那段 */
    storyboard?: Storyboard
    shotNotes?: string[]
  },
): string {
  const profile = i.platform ? getPlatform(i.platform) : null
  /*
   * **镜头脚本要进上下文**（2026-10-04 补）。
   *
   * 上一版这里只写了「【已选模板】××（分镜按它的节奏走，卖点长度要配得上）」——
   * 让模型「配得上」，却不给它配的对象。而「配得上」的依据恰恰是镜头的时间分配：
   * 15 秒 6 镜头与 8 秒 3 段，同一个卖点的写法完全两样（6 镜头里「细节特写」只占
   * 2 秒，卖点得压成一句；三段式的中段占 60% 时长，可以摊开讲）。
   * 不给镜头表，那句话等于没说 —— 跟专业工作流那 18 档「必须给模板骨架」同一条道理。
   */
  const shots = effectiveStoryboard(i.duration, i.storyboard, i.shotNotes)
  return join([
    '这是一支电商带货短视频的上下文。产品是确定的，请把这条片子要讲的内容补齐。',
    '',
    `【产品】${i.productName || '（未填写）'}`,
    `【${i.sceneLabel ?? '视频场景'}】${i.scene}`,
    `【成片参数】${i.duration} 秒 · ${i.aspectRatio} · ${i.style}`,
    i.templateName ? `【已选模板】${i.templateName}` : '',
    '【镜头脚本（这条片子固定的镜头顺序与时间分配 —— 卖点要能塞进对应那一段）】',
    ...shots.map((s) => `· ${s.t}：${s.d}`),
    profile ? `【投放平台】${profile.name}（${profile.region}）` : '',
    profile ? `【文案语言】${profile.copyLanguage}` : '',
    `【产品参考图】${
      (i.refCount ?? 0) > 0
        ? '有（产品外观以参考图为准，卖点不必再描述外观细节）'
        : '没有（只能靠文字描述产品）'
    }`,
    '',
    '【用户已经填的 —— 能沿用就沿用，只在明显与上下文矛盾时才改】',
    `· 卖点与补充要求：${i.sellingPoints || '（空）'}`,
    i.extra ? `· 另注：${i.extra}` : '',
    profile ? '【平台合规】下面这些词与同类宣称一律不许出现：' : '',
    ...(profile ? bannedBlock(profile) : []),
  ])
}

/**
 * 社媒图页的填充上下文 —— 带上渠道的叙事结构与文字占比规则（它们决定卖点该多短）。
 *
 * ⚠️ **不带 `extra`**：这一页没有「补充要求」的输入框。上下文里出现用户填不了、
 * 界面上也看不到的字段，只会让模型以为那是既定前提（同 `api/types.ts` 那条注释）。
 */
export function buildSocialFillBrief(
  i: Pick<SocialInput, 'channel' | 'productName' | 'sellingPoints' | 'audience' | 'scene'> & {
    platform: string
  },
): string {
  const channel = SOCIAL_CHANNELS.find((c) => c.id === i.channel) ?? SOCIAL_CHANNELS[0]
  const profile = getPlatform(i.platform)
  return join([
    `这是一套${channel.name}的上下文。产品是确定的，请把这套图要传达的信息补齐。`,
    `这套图的叙事结构：${channel.structure.join(' → ')}`,
    `文字限制：${channel.textRule}`,
    '',
    `【产品】${i.productName || '（未填写）'}`,
    `【渠道】${channel.name}`,
    `【投放平台】${profile.name}（${profile.region} · 图内文字 ${profile.textLanguage}）`,
    `【平台风格】${profile.styleKeywords.join('、')}`,
    '',
    '【用户已经填的 —— 能沿用就沿用，只在明显与上下文矛盾时才改】',
    `· 卖点：${i.sellingPoints || '（空）'}`,
    `· 目标人群：${i.audience || '（空）'}`,
    `· 场景倾向：${i.scene || '（空）'}`,
    '',
    '【平台合规】下面这些词与同类宣称一律不许出现：',
    ...bannedBlock(profile),
  ])
}

/**
 * 文案页的填充上下文。
 *
 * ⚠️ 这里的产出是**写正文要用的料**，不是正文本身 —— system 那边也写明了
 * 「不要写正文」。理由：用户是来补料的，正文由他点「生成」时按渠道的写作口径
 * 统一产出；在这里先写一段正文，他要么当草稿留着（和后面生成的撞车），
 * 要么删掉（白花钱）。
 */
export function buildCopyFillBrief(
  i: Pick<
    CopyInput,
    'channel' | 'productName' | 'sellingPoints' | 'audience' | 'extra' | 'platform' | 'tone' | 'length'
  >,
): string {
  const profile = getPlatform(i.platform)
  const ch = COPY_CHANNELS.find((c) => c.id === i.channel)
  return join([
    `这是一份要写的「${ch?.name ?? i.channel}」的上下文。产品是确定的，请把它的料补齐。`,
    ch?.description ? `这个渠道要的东西：${ch.description}` : '',
    '',
    `【产品】${i.productName || '（未填写）'}`,
    `【渠道】${ch?.name ?? i.channel}`,
    `【投放平台】${profile.name}（${profile.region}）`,
    `【写作用语】${profile.copyLanguage}`,
    `【语气】${i.tone}`,
    `【篇幅】${i.length}`,
    '',
    '【用户已经填的 —— 能沿用就沿用，只在明显与上下文矛盾时才改】',
    `· 卖点：${i.sellingPoints || '（空）'}`,
    `· 目标人群：${i.audience || '（空）'}`,
    `· 补充要求：${i.extra || '（空）'}`,
    '',
    '【平台合规】下面这些词与同类宣称一律不许出现：',
    ...bannedBlock(profile),
  ])
}

/** 详情页的填充上下文 —— 带上档位与出图模式（卖点要配得上这套图的论证顺序） */
export function buildDetailFillBrief(i: {
  platform: string
  mode?: string
  tier?: string
  productName: string
  sellingPoints: string
  hasRef?: boolean
}): string {
  const profile = getPlatform(i.platform)
  return join([
    '这是一个商品详情页（A+ / 主图组）的上下文。产品是确定的，请把详情页要讲的卖点补齐。',
    '',
    `【产品】${i.productName || '（未填写）'}`,
    `【投放平台】${profile.name}（${profile.region} · 图内文字 ${profile.textLanguage}）`,
    i.mode ? `【出图模式】${i.mode}` : '',
    i.tier ? `【A+ 档位】${i.tier}` : '',
    `【产品参考图】${
      i.hasRef
        ? '有（产品外观以参考图为准，卖点不必再描述外观细节）'
        : '没有（只能靠文字描述产品）'
    }`,
    '',
    '【用户已经填的 —— 能沿用就沿用，只在明显与上下文矛盾时才改】',
    `· 卖点：${i.sellingPoints || '（空）'}`,
    '',
    '【平台合规】下面这些词与同类宣称一律不许出现：',
    ...bannedBlock(profile),
  ])
}

/**
 * 商品库（`/product-assets` 新增 / 编辑抽屉）的填充上下文 —— 标题与卖点。
 *
 * 用户口径（2026-10-08）：「这块可以加一个AI按钮，帮忙填写，因为有时候
 * 只有图片跟简单标题」。所以这一页补的**不是**某一档工作流的「料」，
 * 而是商品档案本身：把四个字的短标题撑成能上架的标题，再把卖点写出来。
 *
 * ### 三条与其余 `build*FillBrief` 不同的地方
 *
 * 1. **必须明说「你看不到图」**。上下文里只有图片的**张数**（以及批量导入
 *    留下的文件名），没有任何画面内容 —— 本项目的 LLM 通道是纯文本的
 *    （`config.LLM_MODEL` = `deepseek-flash`，没有视觉能力，见 `registry._txt`）。
 *    而用户的心理预期恰恰相反：他刚拖进来一张产品图，很自然会以为 AI 看过。
 *    不写这句，模型就会照着「沥水垫」把颜色、图案、表面纹理一起编出来，
 *    而这些字段的下游是**详情页提示词与生图链路** —— 编错一次，整批图都错。
 *
 * 2. **平台可能为空，且这里不能用 `getPlatform()`**。商品库的「平台」是
 *    **可留空**的（留空 = 不限定），而 `getPlatform('')` 会静默回落到默认平台
 *    （`DEFAULT_PLATFORM_ID`）—— 那会让 brief 平白多出一句「【投放平台】淘宝」
 *    和一整张根本无关的禁词表。这里改成**查得到才写**（按 id，再按展示名兜一次，
 *    因为批量导入的 CSV 里用户写的是「淘宝」这种名字而不是 `taobao`）。
 *
 * 3. **语言走 `productLanguageLabel`**，不自己拼中文名。库里存的是 `zh-CN`
 *    这类 id，原样丢给模型它可能当成人名或地区代码；而那份清单的真源在
 *    `config/productAssets.ts`，在这里再写一份映射就是第二份真源。
 */
export function buildProductFillBrief(i: {
  title: string
  sku?: string
  /** 语言 id（`zh-CN`）—— 决定文案用哪种语言写 */
  language?: string
  /** 平台 id 或批量导入带进来的原样字符串；空 = 不限定 */
  platform?: string
  sellingPoints?: string
  /** 产品图**张数**（不是内容 —— AI 看不到图） */
  imageCount?: number
  /**
   * 有原始文件名时才给（批量导入的图有，直接上传的没有）。
   * ⚠️ 它**不含画面信息**，只是弱线索，brief 里必须这么标注。
   */
  imageNames?: string[]
  note?: string
}): string {
  /* ⚠️ 先落成局部常量再查。直接在箭头函数里用 `i.platform`，TS 会丢掉外层三元
     给它的收窄（回调是**后来的求值时机**），报 `'i.platform' is possibly undefined`。 */
  const rawPlatform = i.platform?.trim() ?? ''
  const p = rawPlatform
    ? (PLATFORMS.find((x) => x.id === rawPlatform) ??
      PLATFORMS.find((x) => x.name === rawPlatform) ??
      /* 再兜一次「包含」：批量导入的 CSV 里用户写的是「淘宝」这类简称，
         而档案里的展示名是「淘宝天猫」。`>= 2` 是防呆 —— 单字去 includes
         会撞上一堆平台（「美」「日」在好几个名字里都出现过）。 */
      (rawPlatform.length >= 2
        ? PLATFORMS.find((x) => x.name.includes(rawPlatform))
        : undefined))
    : undefined
  const names = (i.imageNames ?? []).filter(Boolean)
  return join([
    '这是一个**商品档案**的上下文（商品库里的那一条记录），请把标题与卖点补齐。',
    '',
    `【现有标题】${i.title || '（空）'}`,
    i.sku ? `【货号】${i.sku}` : '',
    /* 语言留空时补默认值：`productLanguageLabel('')` 回的是空串，照原样拼出来
       就是一行光秃秃的「【文案语言】」—— 模型多半当它是「不限」然后自选一种，
       而这页表单里那个下拉此刻显示的正是默认语言，界面与 brief 必须是同一份值 */
    `【文案语言】${productLanguageLabel(i.language || DEFAULT_PRODUCT_LANGUAGE)}`,
    /* 平台未知时**整行不写**，而不是写「不限」—— 写「不限」等于把选平台这件事
       交回给模型，它会自己挑一个（多半挑成亚马逊），而那是用户的投放决策 */
    p
      ? `【投放平台】${p.name}（${p.region} · 文案语言 ${p.copyLanguage}）`
      : rawPlatform
        ? `【投放平台】${rawPlatform}（不在平台档案里，按它常见的写法与语言来）`
        : '',
    `【产品图】${i.imageCount ? `${i.imageCount} 张` : '没有'} —— ⚠️ 你**看不到画面**，` +
      '上下文里没有任何图片内容，不许描述颜色 / 图案 / 形状 / 纹理 / logo；',
    names.length ? `【图片文件名】${names.join('、')}（只是文件名，不代表画面内容）` : '',
    '',
    '【用户已经填的 —— 能沿用就沿用，只在明显与上下文矛盾时才改】',
    `· 卖点：${i.sellingPoints || '（空）'}`,
    i.note ? `· 备注：${i.note}` : '',
    ...(p ? ['', '【平台合规】下面这些词与同类宣称一律不许出现：', bannedBlock(p)] : []),
  ])
}

/**
 * 数字人页的填充上下文 —— 口播稿。
 *
 * 与其余四页最大的差别：**这里 AI 的产出就是用户最终要用的东西**（稿子本身），
 * 不是「写别的步骤要用的料」。所以上下文里必须带上**时长**与**用途**
 * —— 15 秒的带货稿和 30 秒的讲解稿，段落数与信息密度是两个物种；
 * 少了这两项，写出来的东西长短全靠运气，用户还得自己删。
 */
export function buildHumanFillBrief(i: {
  productName: string
  script: string
  use?: string
  duration?: number
  platform?: string
  /** 出镜要求（镜头/表情）。2026-10-05 由 `avatarDesc` 改语义而来 —— 见 DigitalHumanInput */
  shotNote?: string
}): string {
  const profile = i.platform ? getPlatform(i.platform) : null
  const use = DIGITAL_HUMAN_USES.find((u) => u.id === (i.use ?? 'sell')) ?? DIGITAL_HUMAN_USES[1]
  return join([
    '这是一条数字人口播视频的上下文。产品是确定的，请把口播稿写出来。',
    '',
    `【产品】${i.productName || '（未填写）'}`,
    `【口播用途】${use.name} —— ${use.description}`,
    i.duration ? `【总时长】${i.duration} 秒（分段要落在这个时长里，超了会被截断）` : '',
    i.shotNote ? `【出镜要求】${i.shotNote}` : '',
    profile ? `【投放平台】${profile.name}（${profile.region}）` : '',
    profile ? `【口播语言】${profile.copyLanguage}` : '',
    '',
    '【用户已经填的 —— 能沿用就沿用，只在明显与上下文矛盾时才改】',
    `· 口播稿：${i.script || '（空）'}`,
    '',
    '【合规】口播是最容易被判定为虚假宣传的形态（它看起来像真人在背书），'
      + '下面这些词与同类宣称一律不许出现：',
    ...(profile ? bannedBlock(profile) : []),
  ])
}

export type FeatureInput = {
  /** 工作流 id，如 clothing-on / product-scene */
  feature: string
  /** 路由 category：fashion | products */
  category?: string
  /** 投放平台 id —— 决定图内文字语言与合规禁词 */
  platform?: string
  /** 用户填的补充要求 / 描述，不同工作流语义不同（背景描述、卖点内容…） */
  requirement: string
  /**
   * 结构化的模特设定文本（只有 AI 模特图那一档有值，见 `config/modelProfile.ts`）。
   * 空串 = 用户全用了「自动 / 不限」，模板里的 `hasModelLook` 就是 false，
   * **整段不输出** —— 不要在这里塞「（未填写…）」这种兜底话术，
   * 「模特设定：不限」对模型没有信息量，只会挤占注意力。
   */
  modelLook?: string
  /**
   * 结构化的场景设定文本（只有场景图那一档有值，见 `config/sceneProfile.ts`）。
   * 空串 = 用户全用了「AI 智能匹配」，模板里的 `hasScene` 就是 false，整段不输出。
   */
  scene?: string
  aspectRatio: string
  resolution: string
  outputs: number
  primaryCount: number
  secondaryCount: number
}

/**
 * 模板变量与开关的**唯一一份**取值。
 *
 * 2026-10-04 抽出。在此之前它们内联在 `buildFeaturePrompt` 里，而
 * `buildFeatureFillBrief`（给 AI 看的骨架）需要**同一套** —— 抽之前两边各算一遍，
 * 迟早出现「AI 照着 A 骨架写，真发出去的是 B」这种对着截图看不出来的错。
 *
 * ⚠️ 这就是铁律 2 的那条：同一份清单不许在两处各写一遍。
 */
function featureTemplateVars(i: FeatureInput) {
  const feature = findFeature(i.category, i.feature)
  const groups = i.category === 'products' ? PRODUCT_FEATURES : FASHION_FEATURES
  const profile = getPlatform(i.platform)

  const vars: TemplateVars = {
    requirement: i.requirement.trim() || '（未填写，按你对该品类的专业判断处理）',
    outputs: i.outputs,
    ratio: i.aspectRatio,
    resolution: i.resolution.toUpperCase(),
    primaryCount: i.primaryCount,
    secondaryCount: i.secondaryCount,
    // 注意：textNote 不带前导「·」——模板正文里已经写了项目符号，这里再带就成「· ·」了
    textNote: feature.textInImage
      ? `画面文字只出现在信息区：${profile.textLanguage}、简短直白、层级清晰，绝不遮挡产品主体`
      : '画面不出现任何文字、水印、边框、logo',
    modelLook: (i.modelLook ?? '').trim(),
    scene: (i.scene ?? '').trim(),
    /**
     * 逐张出图清单 —— 只有 AI 模特图那一档有值（与 `modelLook` / `scene` 同一规矩：
     * 非本档一律空串）。张数 → 出什么，真源在 `config/modelProfile.ts`，
     * 这里只负责按张数取文本，**不在这里再写一套映射**。
     */
    shotPlan: feature.modelLook ? modelShotPlanText(i.outputs) : '',
  }

  const flags: TemplateFlags = {
    hasPrimary: i.primaryCount > 0,
    hasSecondary: i.secondaryCount > 0,
    hasText: Boolean(i.requirement.trim()),
    hasModelLook: Boolean((i.modelLook ?? '').trim()),
    hasScene: Boolean((i.scene ?? '').trim()),
    isApparel: groups === FASHION_FEATURES,
    allowText: Boolean(feature.textInImage),
  }

  return { feature, profile, vars, flags }
}

/**
 * 专业工作流提示词。
 *
 * 正文来自 `prompt-templates.json`（可编辑的唯一真源），这里只负责把表单值喂进去。
 * 上一版是在代码里硬拼两三句 fragment，改一个字要发版，做不了运营侧调优。
 *
 * 平台约束以「抬头块」的形式注入，而不是塞进 14 个模板正文里 ——
 * 模板是给人改的，平台规则跟着档案走，两边分开才不会互相牵制。
 */
export function buildFeaturePrompt(i: FeatureInput): string {
  const { feature, profile, vars, flags } = featureTemplateVars(i)
  const body = renderTemplate(feature.template, vars, flags)

  return join([
    `【投放平台】${profile.name}（${profile.region} · 图内文字 ${profile.textLanguage}）`,
    `【平台风格】${profile.styleKeywords.join('、')}`,
    bannedBlock(profile),
    '',
    body,
  ])
}

/**
 * 留给模型补写的那一格的标记。
 *
 * **故意写得不像提示词** —— 它的生命周期只到这次调用为止：真出图时那一格是
 * 用户确认过的真实文本，标记绝不会流进最终提示词（`buildFeaturePrompt` 不认它）。
 * 用中文全角括号而不是 `{{}}`，是为了让模型一眼看出「这是给我的便签」，
 * 而不是又一个待替换的模板变量。
 */
const FILL_SLOT_MARK = '〔这一格由你补写〕'

/**
 * 专业工作流档的「AI 补全」上下文（`FashionStudio` 那颗按钮，一次 ¥0.10）。
 *
 * ## 为什么这一档必须把**模板正文**一起给模型（前六页都不用）
 *
 * 前六页的提示词是在代码里拼的，模型只要懂各页字段语义就行。这一档不一样：
 * **同一个 `{{requirement}}` 槽位在不同模板里语义完全不同** ——
 * `product.product-main` 里它是「补充要求」，`product.product-selling-point` 里
 * 它是「卖点内容」（一行一条、条数要配出图张数），`fashion.clothing-on` 里
 * 它是「文字补充要求」。只告诉它「工作流叫卖点图」，写出来的东西放哪一档都别扭。
 *
 * 所以给的不是 `{{变量}}` 原文，而是**渲染好的骨架**：其余变量都填实了、
 * 只有那一格换成 `FILL_SLOT_MARK`。模型看到「别的都定稿了、就差这里」，
 * 比看到一堆占位符更清楚要写什么、写多长。
 *
 * ## 三条与其余 `build*FillBrief` 一致的规矩
 * ① **上下文在前端拼** —— 模板与档位定义的真源都在前端（`prompt-templates.json`
 *    与 `catalog.ts`），后端再拼一份就是同一份东西写两遍；
 * ② **带上用户已填的**，并写明「能沿用就沿用」；
 * ③ **带上平台合规禁词** —— AI 写的卖点会原样进提示词，禁词在填的时候就得挡住。
 */
export function buildFeatureFillBrief(i: FeatureInput): string {
  const { feature, profile, vars, flags } = featureTemplateVars(i)

  /* `hasText` 强制为 true：`requirement` 为空时模板里那一行（`{{#if hasText}}`）
     根本不会输出，模型就看不到「这里有格子要填」。渲染骨架时必须让它显形。 */
  const skeleton = renderTemplate(
    feature.template,
    { ...vars, requirement: FILL_SLOT_MARK },
    { ...flags, hasText: true },
  )

  const slotName = feature.textLabel ?? '文字描述'

  return join([
    `这是一个图片工作流的上下文。工作流与出图参数都已经定下来了，只有一格留白 —— 请把它补上。`,
    '',
    `【工作流】${feature.name} —— ${feature.description}`,
    `【要补的那一格在界面上叫】${slotName}`,
    feature.textPlaceholder ? `【界面上给的填写示例】${feature.textPlaceholder}` : '',
    feature.textHint ? `【这一格的规则】${feature.textHint}` : '',
    `【投放平台】${profile.name}（${profile.region} · 图内文字 ${profile.textLanguage}）`,
    `【平台风格】${profile.styleKeywords.join('、')}`,
    `【本次出图参数】${i.outputs} 张 · ${i.aspectRatio} · ${i.resolution.toUpperCase()}`,
    '',
    `【模板骨架 —— 标着「${FILL_SLOT_MARK}」的那一格就是你要写的】`,
    skeleton,
    '',
    '【参考素材】',
    `· 参考图：${
      i.primaryCount > 0
        ? `${i.primaryCount} 张（产品外观以参考图为准，文字不必再描述外观细节）`
        : '没有（只能靠你自己的专业判断，不要凭空描述具体产品特征）'
    }`,
    vars.modelLook ? `· 已设定的模特：${vars.modelLook}` : '',
    vars.scene ? `· 已设定的场景：${vars.scene}` : '',
    '',
    '【用户已经填的 —— 能沿用就沿用，在它基础上补全，不要推倒重写】',
    `· ${slotName}：${i.requirement.trim() || '（空）'}`,
    '',
    '【平台合规】下面这些词与同类宣称一律不许出现：',
    bannedBlock(profile),
  ])
}

export type ToolInput = {
  tool: string
  requirement: string
  aspectRatio: string
  free: boolean
}

export function buildToolPrompt(i: ToolInput): string {
  const t = IMAGE_TOOLS.find((x) => x.id === i.tool)
  /* 键必须与 `IMAGE_TOOLS` 的 id 一一对应（`smoke-img-tools.ts` 会比对两侧）——
     多一条 = 有个已删工具的提示词还留着，少一条 = 那个工具的提示词只剩一句「任务：X」。 */
  const fix: Record<string, string[]> = {
    'remove-bg': ['抠出主体并输出透明背景。', '边缘平滑，保留发丝与半透明材质细节。'],
    'bg-swap': [
      i.requirement ? `替换背景为：${i.requirement}。` : '替换背景为干净的中性背景。',
      '主体本身（形状、颜色、材质、logo）不得改变，边缘不得出现抠图白边或残留原背景。',
      '新背景的光照方向与色温必须与原主体的光影一致，否则会像贴上去的。',
    ],
    /* 两个放大合一：既要「不改变结构」（原 2K 放大那条），
       也要「不出现涂抹感」（原高清放大那条）—— 合成一个工具后这两条都得在。 */
    upscale: [
      '分辨率放大到更高一级，保持原有结构、构图与色彩，不新增或删减元素。',
      '强化纹理与细节清晰度，不产生涂抹感、不改变材质特征。',
    ],
    expand: [i.requirement ? `扩展方式：${i.requirement}。` : '向四周扩展画布。', '补全内容与原图透视、光源、风格一致。'],
    /* 精修同时覆盖产品与人像（原「AI 洗脸」并进来）——
       两条约束各自守住一类主体不被改坏。 */
    retouch: [
      '商业级精修：去瑕疵、均匀肤色与气色、优化光影层次。',
      '产品：保留材质质感与固有色；人像：保留皮肤真实质感与毛孔细节，不做过度磨皮。',
      '五官形状、脸型、发际线、产品结构保持完全一致。',
    ],
    'remove-watermark': ['识别并抹除叠加的水印与文字。', '被覆盖区域按周围纹理自然补全，不留模糊块。'],
  }
  return join([
    `任务：${t?.name ?? i.tool}。`,
    `说明：${t?.description ?? ''}`,
    '',
    ...(fix[i.tool] ?? []).map((x) => `· ${x}`),
    `· 输出比例 ${i.aspectRatio}`,
    '· 不添加任何水印与额外文字',
  ])
}

/**
 * 「只出一张」硬约束。
 *
 * 一次要 N 张时后端提交 N 个独立子任务，每个子任务上游是**独立一次生图**。
 * 如果每个子任务的提示词里都列着整套结构（1 白底正视 2 侧面 3 场景 …），
 * 模型会理解成「把 N 种结构画进同一张画面」—— 于是 N 张结果全是同样的多视角拼贴。
 * 这条约束要跟着每一张走，单张任务同样适用（单张也不该出成拼盘）。
 */
export const ONE_SHOT_RULE =
  '· 整张画面只表现上面这一个视角，一个产品完整占据画面；严禁拼贴、严禁分格、严禁把多个角度或多个产品放进同一张图'


/* -------------------------------------------------------------------------- */
/* 详情页（竖版长图 / 平台官方模块）                                             */
/* -------------------------------------------------------------------------- */

/**
 * 详情页逐屏提示词。
 *
 * ---------------------------------------------------------------------------
 * 这个函数取代了什么（2026-09-29 用户报障「生成提示词太傻了」）
 * ---------------------------------------------------------------------------
 * 上一版详情页走的是**通用逐张模板**：`【本张唯一要求】· 本屏模块：{模块名}
 * · 本屏任务：{一句话}`。加上用户手写的模块顺序这一段，整批 N 份提示词
 * 除模块名以外**逐字相同** —— 模型于是出 N 张几乎一样的图，标题还互相重复。
 *
 * 而且它带着 `ONE_SHOT_RULE`，那句里的「一个产品完整占据画面」本来是
 * **套图/主图**的规则（防把 N 种结构画进一张），用在详情页上直接把
 * 「不要每屏都把完整产品当主体」这条硬规则顶掉了 —— 材质屏、规格屏、
 * 痛点屏全变成了产品定妆图。
 *
 * 现在每屏的提示词由三段构成，缺一不可：
 *   1. **共用头部**（页面拼好传进来）：平台规格、商品、参考图保真、合规、设计强度锁；
 *   2. **全页叙事线**（每屏都带）：这一篇在讲什么、买家依次要被回答什么问题、
 *      跨屏连贯的硬约束 —— 带着它，模型才知道自己这屏在整篇里的位置，
 *      这是「连贯」唯一的来源；
 *   3. **本屏唯一任务**：这一屏回答哪个问题、承接上一屏什么、给下一屏留什么、
 *      画面主体是什么、构图尺度、完整产品出不出现、反面清单。
 *
 * 逐屏列表里**只写买家问题，不写官方模块名** —— 模块名是内部规格用语
 * （「Image Header with Text · 开场」），对生成没有任何帮助，
 * 写进去只会让模型把界面用语当画面内容。
 */
export type DetailPromptInput = {
  /** 页面拼好的共用头部 */
  header: string
  /** 本批逐屏解构 —— 由 `planDetailShots` 产出，页面与这里共用同一份 */
  shots: PlannedShot[]
  /** 兜底比例（某屏 size 为空时用），来自平台档案 */
  fallbackRatio: string
}

/** 尺寸的界面写法：像素写 `970×600`，比例原样写（与平台规格层同一套规则） */
const sizeText = (s: string) => (isPixelSize(s) ? displaySize(s) : s)

export function buildDetailPrompts(i: DetailPromptInput): string[] {
  const shots = i.shots
  const n = shots.length
  if (!n) return []

  const plan = describePlan(shots)

  /*
   * 叙事线 —— 全篇规划，**每一屏都带**。
   * 措辞刻意用「买家依次要被回答的问题」而不是「模块顺序」：前者是叙事，
   * 后者是清单。清单式措辞会让模型理解成「按顺序画这 N 个东西」，
   * 正是「每屏都是产品定妆」的来源。
   */
  const narrative = [
    '',
    `【全页叙事线】本批 ${n} 屏是**一篇连贯的购买说服页**，不是 ${n} 张互不相干的单图。`,
    `· 购买决策类型：${plan.decisionType}`,
    `· 页面策略：${plan.strategy}`,
    `· 最佳首屏方向：${plan.firstScreen}`,
    `· 视觉节奏：${plan.rhythm}`,
    '',
    '买家从点进来到下单，依次要被回答这几个问题（你只画其中一屏，但必须知道自己在整篇里的位置）：',
    ...shots.map((s, idx) => `${idx + 1}. ${s.question}`),
    '',
    '【产品类型策略】先判断本产品属于哪一类，再按那一类的逻辑组织这一屏的证据：',
    ...PRODUCT_KIND_GUIDE.map((r) => `· ${r}`),
    '',
    '【跨屏连贯要求】',
    '· 相邻两屏必须**承接**：这一屏只回答它自己那个问题，别的留给别的屏，不要每屏都从头讲一遍',
    '· 卖点要**分配**：同一个卖点只在最合适的那一屏作为主证据出现，禁止在每一屏重复罗列全部卖点',
    '· 相邻两屏不得使用同一构图（同背景、同机位、同主体位置、同标题位置）',
    '· 连续三屏都做成「产品居中 + 左上大标题 + 底排小标签」是本批的**明令禁止项**',
    '· 全篇色调、光线、镜头语言保持一致，像同一次拍摄；但取景与构图尺度必须逐屏变化',
  ].join('\n')

  return shots.map((s, idx) => {
    const prev = idx > 0 ? shots[idx - 1] : null
    const next = idx < n - 1 ? shots[idx + 1] : null

    /*
     * 与邻屏的关系 —— **不能一律写成「承接」**。
     *
     * 一个模块可能对应多个图位（Amazon A+ 的三图模块是 3 格 300×300、
     * 单图+侧栏是 2 格）。这三格回答的是**同一个**买家问题、是并列的证据，
     * 不是「第 3 屏讲完痛点、第 4 屏接着讲机制」那种顺序关系。
     * 一律写「承接：上一屏已经在讲 X，不要重复它」，模型就会以为
     * 后两格该换个话题 —— 真跑出来是「三格画成同一张图」或「三格各讲各的」，
     * 两种都不对。所以同模块的邻屏改写成「并列格」。
     *
     * 移动端资产同理：它是**上一屏的竖屏版**，不是新的一屏，
     * 说成「承接」会让模型以为要画个新内容。
     */
    const sameModulePrev = Boolean(prev && prev.moduleName === s.moduleName)
    const sameModuleNext = Boolean(next && next.moduleName === s.moduleName)

    const prevLine = !prev
      ? '· 这是全篇第一屏：给一个让人愿意往下看的画面，不要试图在这一屏把所有事说完'
      : sameModulePrev && s.isMobile
        ? `· 这一屏是第 ${prev!.no} 屏的**移动端版本**：内容与它一致，但构图必须为竖屏重做，禁止拿桌面图裁切或拉伸`
        : sameModulePrev
          ? `· 与上一屏（第 ${prev!.no} 屏）同属一个模块：回答的是**同一个**买家问题，这一格必须换角度或换证据 —— 两格不能是同一张图的微调`
          : `· 承接：上一屏（第 ${prev!.no} 屏）已经在讲「${prev!.question}」，这一屏不要重复它，直接接着往下答`

    const nextLine = !next
      ? '· 这是全篇最后一屏：收住，不要再抛出新的卖点'
      : sameModuleNext && s.isMobile
        ? ''
        : sameModuleNext
          ? `· 与下一屏（第 ${next!.no} 屏）同属一个模块：两格并列回答同一个问题，构图与证据必须各不相同`
          : `· 引出：下一屏（第 ${next!.no} 屏）要讲「${next!.question}」，这一屏为它留出缺口，不要把话说满`

    return join([
      i.header,
      narrative,
      '',
      `【本屏唯一任务｜第 ${s.no} / ${n} 屏】`,
      `· 这一屏替买家回答：${s.question}`,
      prevLine,
      nextLine,
      `· 画面主体：${s.subject}`,
      `· 构图尺度：${SCALE_NAME[s.scale]}`,
      s.fullProduct
        ? '· 完整产品：这一屏以完整产品为主体'
        : '· 完整产品：**这一屏不要以完整产品为主体** —— 主体是场景或局部，产品最多作为配角出现',
      `· 这一屏要完成的事：${s.task}`,
      `· 反面清单：${s.avoid}`,
      `· 出图尺寸：${sizeText(s.size || i.fallbackRatio)}${
        ratioHint(s.size) ? `（宽高比 ${ratioHint(s.size)}）` : ''
      } —— 画布宽高比严格按它，不要套用本批其它屏的比例`,
      ...(s.isMobile ? ['· 这一份是移动端资产：与桌面图分别交付，禁止裁切或拉伸得到'] : []),
      s.textFree
        ? '· 画面文字：本屏画布内不得出现任何文字、水印、边框、logo（该平台合规硬约束，出现即需下架）'
        : '· 画面文字：按上面平台的文字语言与排版规则执行，标题短而具体，不要长句',
      /* 详情页的「只画一个画面」—— 保留防拼贴，**去掉**「一个产品完整占据画面」：
         那半句是套图规则，放在详情页会把材质屏/痛点屏/规格屏全顶成产品定妆图。 */
      '· 整张画面只画这一个画面：严禁拼贴、严禁分格、严禁把别的屏的内容塞进来',
    ])
  })
}

export type VideoInput = {
  scene: string

  /**
   * `scene` 那一行在提示词里的**前缀词**，默认「场景类型」。
   *
   * 复刻档（`/video-replicate`）的 `scene` 存的是**复刻方式**（结构复刻 / 节奏复刻），
   * 不是画面场景（清单见 `config/videoModes.ts` 那一档的 `scenes`）。同一个字段
   * 在两个档下是两个语义，而提示词里那行前缀是写给模型看的 ——
   * 「场景类型：结构复刻」会让模型去找一个叫这个名字的画面场景
   * （2026-10-08 用户口径：「复刻这个…这个搞笑吧」）。
   *
   * ⚠️ 只在**输出那一个词**上分档，`scene` 字段本身不动：它的值要提交、
   * 要进草稿、要落库，换字段名等于把三处一起改。
   */
  sceneLabel?: string

  productName: string
  sellingPoints: string
  duration: number
  aspectRatio: string
  style: string
  extra: string
  platform?: string
  /**
   * 这条片子自己的镜头脚本。**传了模板就用模板的**，
   * 不传退回 `GENERIC_BEATS` 那套商品视频叙事。
   * 两种形态都收（三段式 / 多镜头导演分镜），见 `Storyboard`。
   */
  storyboard?: Storyboard
  /**
   * **镜头细化描述** —— 一行一段，覆盖模板分镜里「这一段拍什么」的那一句。
   *
   * 为什么要有这一层（2026-10-04）：模板的分镜是**通用的**（「产品定格特写，
   * 快速推进制造视觉钩子」），换成具体产品就偏空 —— 而这一步以前只有两条路，
   * 要么手改（用户得自己会写分镜），要么让 AI **整份重写提示词**
   * （连「3×3 等分」「镜号 1-9」这些硬约束一起过模型的手，改坏了看不出来）。
   *
   * 现在改成「AI 只写镜头这一层」，锁区由模板原样拼回 —— 跟专业工作流那 18 档
   * 同一套机制（见 `buildFeatureFillBrief`）：**契约不经过模型的手**。
   *
   * 三条规矩：
   * - **空数组 = 不覆盖**（用模板原本的分镜）—— 可回滚，不带这条就不改变现有行为；
   * - **长度与镜头数不符 = 忽略**（换模板、改时长都会变镜头数）—— 宁可用回模板版，
   *   也不要把 A 模板的细化描述错位贴到 B 模板的镜头上；
   * - **分镜图提示词与视频提示词共用这一份**（都经 `effectiveStoryboard`）——
   *   在别处再写第二份，就会出现「分镜图 9 格讲 A、视频提示词却在讲 B」。
   */
  shotNotes?: string[]
  /**
   * **分镜出图**时带了几张产品图（排在最前）—— 分镜提示词里要说明角色。
   *
   * ⚠️ 2026-10-09 用户口径「参考图应该是分镜，而不是产品」之后，产品图 / 场景图
   * **不再上送给视频模型**（生成视频只发那张九宫格分镜图），改去喂**分镜出图**
   * ——见 `buildStoryboardPrompt`。所以这两个计数只有那条提示词在用。
   */
  refProductCount?: number
  /** 分镜出图时带了几张场景图（紧接产品图之后）—— 从「内容与场景」页传的那一档 */
  refSceneCount?: number
  /**
   * 是不是带着一张**九宫格分镜参考图**（视频页「分镜参考图」出的那张）。
   *
   * ⚠️ 2026-10-09 起它是**生成视频时唯一的一张参考图** —— 产品图 / 场景图都不再
   * 跟着上送。为什么必须写它的角色：不写的话提示词里没有任何一句拦得住模型
   * **把九宫格原样搬进画面**（它看到的就是一张画了 9 格的参考图），
   * 成片会得到一格一格的拼贴。
   */
  refStoryboard?: boolean
  /**
   * 参考视频条数。**> 0 表示这条片子是「复刻」** —— 镜头设计以参考视频为准。
   *
   * 为什么必须单列（2026-10-06）：参考视频此前**只是被当作素材发出去**，
   * 提示词里一个字都没提它。于是模型手里拿着视频、提示词却写着
   * 【镜头脚本】「产品定格特写 → 使用过程 → 产品+场景收尾」（`GENERIC_BEATS`），
   * 两条指令打架 —— 而**提示词永远更具体**，模型照它拍，成片看起来就是
   * 「套了通用模板」，跟参考视频毫无关系。视频复刻档「必传参考视频」
   * 却在提示词里从不引用它，等于白传。
   *
   * ⚠️ 判据是**条数**而不是「当前是不是复刻档」：普通视频档在
   * `ref_video_max > 0` 的模型上也能传参考视频（控件 hint 写的就是
   * 「用于复刻运镜与节奏」），那些档同样要按复刻处理。
   */
  refVideoCount?: number
  /**
   * 参考视频的**逐镜头结构** —— 复刻档第 1 步分析出来的那份（见 `VideoAnalysisCard`）。
   *
   * 与 `refVideoCount` 是配套的两件事，**缺一不可**：`refVideoCount` 只是说
   * 「有这条片子、以它为准」，而模型拿到片子之后仍然得自己把镜头看出来。
   * 这份分析是我们先替它看一遍的结论 —— 有了它，提示词里才有「第 3 个镜头是
   * 低角度推进拍鞋底」这种具体到能照着拍的东西。
   *
   * ⚠️ **过期的那份一个字都不许进来**。判据（`liveVideoAnalysis`）由调用方负责 ——
   * 本函数收到的必须是**已经对得上当前参考视频**的那一份，见 `VideoStudio` 的
   * `analysisForPrompt`。把判定塞进来这里就变成了第三处实现。
   */
  videoAnalysis?: VideoAnalysisInput | null
}

/**
 * 拼提示词真正要用的那三个字段 —— **提示词侧的最小口径**。
 *
 * ⚠️ 刻意**不复用 `api/types.ts` 的 `VideoAnalysis`**：本文件是纯配置层，
 * 只依赖 `./config/*`（见文件头）。`videoUrl`（失效判定用）与 `shotCount`
 * （清单条数的显示值）都是**草稿层**的事，与提示词一个字都无关；在这里照抄一份
 * 只会让「哪天那边加字段」这件事看起来必须同步。
 *
 * 结构类型是宽进严出的：调用方直接传一个 `VideoAnalysis` 也能通过。
 */
export type VideoAnalysisInput = {
  /** 整体节奏一句话 */
  rhythm: string
  /** 画面风格一句话 */
  style: string
  /** 逐镜头画面描述，按顺序。空行由 `buildVideoPrompt` 自己滤掉 */
  shots: string[]
}

/** 三段式镜头脚本的文案（起 / 中 / 收）。时间轴由 `videoStoryboard` 按时长算 */
export type StoryboardBeats = { open: string; body: string; close: string }

/** 一段镜头：时间段位 + 这一段拍什么。与 `videoStoryboard` 的返回值同形 */
export type StoryboardShot = { t: string; d: string }

/**
 * 多镜头导演分镜 —— **每镜头自带时间段位**，不由时长推导。
 *
 * 为什么三段式之外还要有这一形态（2026-09-30）：
 * 三段式的时间轴是 `videoStoryboard` 按「2 秒 / 60% / 余下」切出来的，天生只有
 * 「开头 / 中段 / 收尾」三层 —— 描述「街拍走位」「卡点舞」这类**单一玩法**刚好够，
 * 但描述不了**一支结构完整的带货片**。专业带货脚本（参照悟空 Seedance 那套）
 * 15 秒是 6 个镜头：钩子 → 产品外观 → 使用动作 → 细节特写 → 场景 → CTA，
 * 其中「细节特写」只占 2 秒。硬塞进三段式，用户拿到的是「中段 2-9s 一路使用过程」
 * 这种糊在一起的描述，等于没有节奏。
 *
 * ⚠️ **只加形态、不加真源**：两种形态都从 `videoStoryboard` 出去，提示词的
 * 【镜头脚本】、右栏分镜卡、九宫格分镜图仍然读同一份数据 —— 在别处再写第二份
 * 镜头清单，就会出现「分镜图 9 格、视频提示词 3 段」两套叙事打架。
 */
export type StoryboardShots = { shots: StoryboardShot[] }

/** 模板自带的分镜 —— 三段式或多镜头，二选一（联合类型让 tsc 拦住写歪的第三种形态） */
export type Storyboard = StoryboardBeats | StoryboardShots

/**
 * 判形态。
 *
 * ⚠️ 用 `Array.isArray(s.shots)` 而不是 `'open' in s`：后者的反例是**空对象**
 * （既没有 open 也没有 shots），会同时被两边认下、也能同时被两边排除。
 */
export function isStoryboardShots(s: Storyboard): s is StoryboardShots {
  return Array.isArray((s as StoryboardShots).shots)
}

/**
 * 不带模板时的通用三行。**只描述「商品视频」这一种叙事**
 * （产品定格 → 使用过程 → 产品+场景收尾），所以它只配当兜底。
 */
const GENERIC_BEATS: StoryboardBeats = {
  open: '产品定格特写，快速推进制造视觉钩子',
  body: '使用过程实拍，突出真实使用感',
  close: '产品 + 场景收尾，画面稳定，下方保持干净',
}

/**
 * 镜头脚本（分镜）—— **单一真源**。
 *
 * 这份时间轴以前写了两遍：本文件 `buildVideoPrompt` 的【镜头脚本】段一份、
 * `VideoStudio` 右栏那张「分镜脚本」卡片里又一份，措辞还对不上
 * （「快速推进制造视觉钩子」vs「快速推进制造钩子」，以及收尾句的差异）。
 * 用户一改时长就得记着改两处 —— 铁律「同一份清单不许在两处各写一遍」的复发点。
 *
 * 现在提示词和右栏预览都调用这里，改一处两处同时生效。
 *
 * ## 为什么第二参数是 `beats`（2026-09-29 补）
 *
 * 只吃 `duration` 的版本让 6 条带货短视频模板拿到了**逐字相同**的分镜：
 * 「会动的服装」和「学功夫练武术舞蹈」都是「0-2s 产品定格特写 →
 * 2-9s 使用过程实拍，手持运镜 → 9-15s 产品+场景收尾」——
 * 而武术那条的运镜明明选了**固定机位**，分镜里却写着「手持运镜」；
 * 「实景动画」要求街道空镜开场、产品末段才插入，分镜却让它 0-2s 就定格特写。
 *
 * 用户点模板后提示词只差三个词，等于换了个名字、画面一模一样。
 * 所以模板必须**自带**这三段文案（见 `videoTemplates.ts` 的 `storyboard` 字段），
 * 这里只负责把文案贴到时间轴上。
 */
export function videoStoryboard(duration: number, sb?: Storyboard): StoryboardShot[] {
  /*
   * 多镜头：时间轴是模板自己写死的，**这里一个字都不改**。
   * 15 秒 6 镜头里「细节特写只占 2 秒」是脚本设计的一部分，
   * 按 60% 重算会把它拉成 3 秒，整条片子的节奏就散了。
   */
  if (sb && isStoryboardShots(sb)) return sb.shots
  const b = sb ?? GENERIC_BEATS
  const mid = Math.max(3, Math.round(duration * 0.6))
  return [
    { t: '0-2s', d: b.open },
    { t: `2-${mid}s`, d: b.body },
    { t: `${mid}-${duration}s`, d: b.close },
  ]
}

/**
 * 把「镜头细化描述」叠到分镜上 —— **两条提示词唯一的那份镜头数据的出口**。
 *
 * 分镜图提示词（`buildStoryboardPrompt`）与视频提示词（`buildVideoPrompt`）
 * 都从这里取镜头，所以两处的段数、时间轴、每段拍什么永远一致。各自去调
 * `videoStoryboard` 再各叠一次的话，只要有一边忘了叠，就会出现
 * 「分镜图画的是细化版、成片拍的是模板原版」—— 对着截图看不出来。
 *
 * 长度不符就整体退回模板版：宁可用回通用分镜，也不要把 A 模板的细化描述
 * 错位贴到 B 模板的镜头上（换模板、改时长都会让镜头数变）。
 */
export function effectiveStoryboard(
  duration: number,
  sb: Storyboard | undefined,
  notes: string[] | undefined,
): StoryboardShot[] {
  const shots = videoStoryboard(duration, sb)
  if (!notes || notes.length !== shots.length) return shots
  return shots.map((s, idx) => {
    const d = (notes[idx] ?? '').trim()
    return d ? { t: s.t, d } : s
  })
}

export function buildVideoPrompt(i: VideoInput): string {
  const profile = getPlatform(i.platform)
  /*
   * 口播与否**由场景决定，不能写死**。
   *
   * 【技术要求】以前无条件写「无口播」，而场景清单里有「纯带货口播」这类
   * 天生要人声的档位 —— 用户选了它，提示词里同时出现「纯带货口播」和
   * 「无口播」两条相反的指令，模型只能自己挑一条。
   * （平台档案的卖点规则里本来就有「口播稿 ≤ 60 字」的硬限制，两边也自相矛盾。）
   */
  const voiceOff = !i.scene.includes('口播')
  /*
   * 参考图的**角色说明**。
   *
   * ⚠️ 2026-10-09 用户口径「参考图应该是分镜，而不是产品」：生成视频时
   * **只发那张九宫格分镜图** —— 产品图 / 场景图不再上送给视频模型
   * （它们改去喂**分镜出图**，见 `buildStoryboardPrompt`）。
   * 所以这里只剩分镜图一段，不再有「前 N 张是产品图」那种话 ——
   * 说了就是让模型去找一张根本没发过去的图。
   *
   * 这段必须留着、且必须写死「不要照抄拼贴形式」：它本身就是一张画了 9 格的
   * 参考图，不拦的话模型会连格线一起复刻，成片变成一格一格的拼贴
   * （这是这类参考图最典型的翻车法）。
   */
  const refBlock = i.refStoryboard
    ? [
        '【参考图用法】',
        '· 本次唯一一张参考图是【九宫格分镜参考图】：它把 9 个镜头排成 3×3，只用来确定镜头顺序、景别推进与构图节奏。成片必须是一支连续的长镜头级画面流 —— 不要出现分格、格线、镜号或任何拼贴形式，也不要把它当成要逐格复现的画面。',
      ].join('\n')
    : ''
  /*
   * 参考视频的**角色说明 + 优先级**。
   *
   * 与上面 `refBlock` 是并列的两件事：参考图管「长什么样」，参考视频管「镜头怎么走」。
   * 三条都必要，少一条就会翻车：
   * · 只说「有一条参考视频」而不说它是**最高优先级** —— 提示词里的【镜头脚本】
   *   更具体，模型会挑它，参考视频等于没给；
   * · 不说「只借镜头、不搬内容」 —— 模型会把参考视频里的产品和人物一起复刻进来，
   *   成片卖的是别人家的货；
   * · 不说「冲突时以它为准」 —— 下面的【镜头脚本】那段还在，两条指令打架时没有裁决人。
   */
  const nv = i.refVideoCount ?? 0
  const refVideoBlock = nv
    ? [
        '【参考视频用法 · 最高优先级】',
        `· 随本次任务提供了 ${nv} 条参考视频，它是**镜头设计的唯一依据**：机位、景别、镜头顺序、切换时机与整体节奏全部照它来。`,
        '· 与提示词里其他任何关于镜头、运镜、分镜的描述冲突时，**一律以参考视频为准**（包括下面的【镜头脚本】一段）。',
        '· 但参考视频里的产品、人物、文字与 logo 一律**不要照搬** —— 只借「镜头怎么走」，画面内容按上面的【产品】与场景重新生成。',
      ].join('\n')
    : ''
  /*
   * 参考视频的**结构分析**（2026-10-08）。
   *
   * `refVideoBlock` 那句「机位、景别、镜头顺序全照它来」是**指令**，签的是
   * 一个我们自己都没看过的片子 —— 模型得自己把 15 秒里的 14 个镜头看出来，
   * 看漏了、切错点，提示词这边没有任何东西能发现。所以复刻档第一步先用
   * `qwen-vl-max` 把片子拆成「节奏 + 风格 + 逐镜头清单」（见 `VideoAnalysisCard`），
   * 这份清单就在这里进提示词：**结论以文字再讲一遍**，模型照着拍比它自己数稳。
   *
   * 两条边界：
   * · **`nv > 0` 才输出**。分析是从参考视频来的，没有参考视频却在提示词里讲
   *   「参考视频结构」，模型会去找一条不存在的片子（而它收到的素材里也没有）。
   *   正常路径上两者同进同出，这里是**防串档的兜底**：草稿里留着上次那份、
   *   而参考视频已被删掉时，宁可不写，也不要写一段无主的结构。
   * · **空清单不算有分析**。一条都没解析出来时（`parsed: false` 那次的产物）
   *   只输出一行「共 0 个」的话，等于告诉模型「这片子没有镜头」。
   */
  const na = nv > 0 ? i.videoAnalysis ?? null : null
  const analysisShots = (na?.shots ?? []).map((s) => s.trim()).filter(Boolean)
  const analysisBlock =
    na && analysisShots.length
      ? [
          '【参考视频结构】已逐镜头拆解 —— 复刻时按这份结构走：',
          ...(na.rhythm.trim() ? [`· 整体节奏：${na.rhythm.trim()}`] : []),
          ...(na.style.trim() ? [`· 画面风格：${na.style.trim()}`] : []),
          `· 镜头顺序（共 ${analysisShots.length} 个，按下面的先后依次出现）：`,
          ...analysisShots.map((s, idx) => `  ${idx + 1}. ${s}`),
          /* 最后这句是**给裁决用的**：上面这段比【参考视频用法】具体，
             而且比参考视频本身更好读，模型有可能反过来把它当唯一依据。
             所以明写它只是一份「读数」，机位与切换时机仍以片子为准 ——
             `refVideoBlock` 那句「冲突时以参考视频为准」在这里得到落点。 */
          '· 上面每一条只说「这个镜头拍什么」；具体机位、景别与切换时机仍以参考视频本片为准，不要因为这里写得细就改成按这份清单自由发挥。',
        ].join('\n')
      : ''
  /*
   * 正文改从 `prompt-templates.json` 的 `video.script` 取（2026-10-04）。
   *
   * 上一版是在这里 `join([...])` 硬拼的，后果有两个：改一个字要发前端版本；
   * 而且**锁区与自由区混在同一个字符串里**，所以「让 AI 再写一版」只能是整份重写 ——
   * 连「无口播」「色彩统一」这类硬约束一起过模型的手，改坏了对着截图看不出来。
   * 现在正文在 JSON 里（运营能直接改），镜头那一层单独交给
   * `effectiveStoryboard`（入参 `shotNotes`）。
   *
   * 多行变量（`refBlock` / `sellingPlan` / `shotList`）传进去之前先拼成字符串 ——
   * 模板语法刻意只支持「变量 + 条件块」，不给它数组与循环。
   */
  return renderTemplate(
    'video.script',
    {
      duration: i.duration,
      platformName: profile.name,
      scene: i.scene,
      /* 复刻档传「复刻方式」—— 它的 scene 是复刻方式而不是画面场景 */
      sceneLabel: i.sceneLabel ?? '场景类型',
      style: i.style,
      platformStyle: profile.styleKeywords.join('、'),
      productName: i.productName || '（未填写）',
      sellingPoints: i.sellingPoints,
      refBlock,
      refVideoBlock,
      analysisBlock,
      extra: i.extra,
      sellingPlan: sellingPointBlock(profile, i.sellingPoints).join('\n'),
      /*
       * 有参考视频时**不给通用分镜** —— `GENERIC_BEATS` 那三段
       * （「产品定格特写 → 使用过程 → 产品+场景收尾」）是一套**别的片子的结构**，
       * 摆在【镜头脚本】里就是跟参考视频抢方向盘，而且它更具体、多半会赢。
       * 这里改成一句指路的说明，让【镜头脚本】这一段不再与参考视频矛盾。
       */
      shotList: nv
        ? '**本片按参考视频复刻**：镜头顺序、机位、景别与切换时机全部照参考视频执行，不采用通用分镜模板。'
        : effectiveStoryboard(i.duration, i.storyboard, i.shotNotes)
            .map((s) => `${s.t}：${s.d}`)
            .join('\n'),
      ratio: i.aspectRatio,
      platformVideoSize: profile.sizes.video,
      voiceRule: voiceOff
        ? '无口播，背景音乐由后期添加；画面上不出现任何文字（含字幕）'
        : '有口播（按上面卖点规则的字数限制念白），背景音乐由后期添加；画面上同样不出现任何文字（含字幕）',
    },
    {
      hasSellingPoints: Boolean(i.sellingPoints),
      hasExtra: Boolean(i.extra),
      hasRefVideo: nv > 0,
      /*
       * 判据是**块本身非空**，不是 `Boolean(i.videoAnalysis)` ——
       * 有分析对象但清单被用户删空了（或那次没解析出来）时块是空串，
       * 开关却亮着，模板里就会留下一段只有标题的【参考视频结构】。
       */
      hasAnalysis: Boolean(analysisBlock),
    },
  )
}

/**
 * 九宫格分镜参考图提示词 —— 视频页第 3 步「分镜参考图」用（2026-09-29 新增）。
 *
 * ## 它和 `buildVideoPrompt` 是什么关系
 *
 * **同一份镜头脚本的两种画法**，不是两份不同的分镜：
 * `buildVideoPrompt` 把 `videoStoryboard` 的三段摊在【镜头脚本】里交给视频模型；
 * 这里把**同样三段**各摊成 3 格、凑成 9 格交给生图模型，要的是一张
 * 「拍摄前的镜头参考图」。所以九宫格的结构完全由 `videoStoryboard` 决定 ——
 * 在别处再写一份 9 条镜头清单，就会出现「分镜图上是 9 个镜头、
 * 视频提示词里是另外 3 段」这种两套叙事打架。
 *
 * ## 为什么要求「3×3 等分 + 每格一个镜号」
 *
 * 不写等分约束时，生图模型会把九宫格画成一张**连续的大画面**（九格之间没有边界），
 * 用户拿到的就是一张普通构图图，看不出镜头顺序；不写镜号则九格顺序无法核对。
 * 这两条是这张图**唯一有价值的两个信息**，所以必须显式要求。
 *
 * ## 每格比例 = 整图比例（不是笔误）
 *
 * 3×3 等分时整图宽高比恒等于单格宽高比（3 列 × 3 行，倍数互相抵消），
 * 所以整图直接用片子的 `aspectRatio`，得到的每一格正好就是成片的画幅 ——
 * 传 1:1 的话每格会变成方画幅，与竖版成片对不上。
 */
/**
 * 把 `cells` 格按 `shots` 个镜头分匀 —— 余数从**靠前**的镜头开始各加一格。
 *
 * 3 个镜头 → `[3,3,3]`（与「每段 3 格」的旧行为逐字一致，所以三段式模板的
 * 分镜提示词一个字都没变）；6 个 → `[2,2,2,1,1,1]`：钩子 / 产品 / 使用这三个
 * 镜头信息量最大，余数给它们，细节与收尾各占一格就够。
 *
 * 格数**必须用完且不越界** —— 少分一格就会算出「第 16-18 格」这种九宫格里
 * 根本不存在的格位，提示词与「3×3 共 9 格」自相矛盾。镜头数上限由
 * `smoke-prompts` 的断言兜住（模板侧不允许超过 9 个镜头）。
 */
function allocateCells(shots: number, cells: number): number[] {
  const base = Math.floor(cells / shots)
  const rest = cells % shots
  return Array.from({ length: shots }, (_, i) => base + (i < rest ? 1 : 0))
}

export function buildStoryboardPrompt(i: VideoInput): string {
  const profile = getPlatform(i.platform)
  /* 与 `buildVideoPrompt` 走同一个出口 —— 两处的段数、时间轴、每段拍什么必须一致 */
  const shots = effectiveStoryboard(i.duration, i.storyboard, i.shotNotes)
  const multi = !!i.storyboard && isStoryboardShots(i.storyboard)
  /**
   * 九宫格**永远是 9 格**，而镜头数不一定是 3 —— 所以要按镜头数把 9 格分匀。
   * 量词跟着形态走：三段式叫「第 N 段」（一段铺 3 格），多镜头叫「镜头 N」——
   * 6 镜头下每格就是一个镜头，再叫「段」会与上面「每格画一个镜头」对不上。
   */
  const perShot = allocateCells(shots.length, 9)
  let cursor = 1
  const cells = shots.map((s, idx) => {
    const n = perShot[idx]
    const from = cursor
    cursor += n
    const where = n === 1 ? `第 ${from} 格` : `第 ${from}-${from + n - 1} 格`
    return `· ${multi ? `镜头 ${idx + 1}` : `第 ${idx + 1} 段`}「${s.t}」→ ${where}：${s.d}`
  })
  /*
   * 产品图 / 场景图的**角色说明**（2026-10-09 用户口径「出分镜时带产品图」）。
   *
   * 这条之前写在**视频**提示词里（那时视频直接收产品图、场景图）；改成
   * 「生成视频只发分镜图」之后，产品图 / 场景图改从这里进生图模型 ——
   * 所以角色说明也跟着搬过来，否则模型收到两张图却不知道是干什么的，
   * 会把场景图里的人物也画进产品、或把产品图当场景照抄。
   *
   * 为什么这一段在分镜这一步尤其要紧：九宫格是**开拍前的核对物**，
   * 产品画错了用户当场就能看出来；而它又是视频唯一的视觉参考，
   * 分镜里的产品不像，成片里的产品就不像。
   */
  const np = i.refProductCount ?? 0
  const ns = i.refSceneCount ?? 0
  const refBlock =
    np + ns > 0
      ? [
          '【参考图用法】',
          ...(np
            ? [
                `· 前 ${np} 张是【产品图】：九格里出现的产品都必须严格按它还原外观（形状 / 颜色 / 材质 / 比例），九格是同一个产品，不要改动它`,
              ]
            : []),
          ...(ns
            ? [
                `· 紧随其后的 ${ns} 张是【场景参考图】：只取它的场景、氛围、构图与光线；图中的人物、文字、产品一律不要照搬`,
              ]
            : []),
        ].join('\n')
      : ''
  /* 正文同样来自 `prompt-templates.json`（`video.storyboard`），理由见 `buildVideoPrompt` */
  return renderTemplate(
    'video.storyboard',
    {
      duration: i.duration,
      platformName: profile.name,
      productName: i.productName || '（未填写）',
      sellingPoints: i.sellingPoints,
      scene: i.scene,
      style: i.style,
      platformStyle: profile.styleKeywords.join('、'),
      ratio: i.aspectRatio,
      shotCells: cells.join('\n'),
      refBlock,
    },
    {
      hasSellingPoints: Boolean(i.sellingPoints),
    },
  )
}

/**
 * 「这组输入」的指纹 —— 手改版提示词与镜头细化**都绑定在它上面**（2026-10-05）。
 *
 * ## 它存在的理由（用户两次报障，第二次才找对病根）
 *
 * 分镜提示词允许手改（`storyboardPrompt`）、镜头描述允许 AI 细化（`shotNotes`），
 * 两者都是**针对某一个产品、某一个模板**写出来的。而它们存在**页面级草稿**里 ——
 * 那个草稿跨产品、跨会话活着。于是 `sbPrompt = 手改版 || 自动版` 里手改版一挂上去，
 * 换产品 / 换模板 / 改时长之后提示词**一个字都不变**，九宫格也跟着不失效。
 *
 * 用户原话：「为什么这个不会跟着之前的产品的信息改变的？」
 * 第二次的截图更直接：选了「卡点换装」模板、分镜图还是上一个产品的水壶。
 *
 * ## 为什么是「指纹」而不是逐个字段去比对
 *
 * 逐个比要维护一张「哪些字段算数」的清单，且必然漏 —— 上一版漏的正是**模板**
 * （它改的是镜头结构，从文案里那几项比对不出来）。指纹只回答一件事：
 * **这组输入是不是当初写这份东西的那一组**。清单一律从入参取，
 * 改了 `buildStoryboardPrompt` 的入参，这里跟着改，不存在第二份定义。
 *
 * ⚠️ 只收**真正进提示词的字段**。换模型 / 换清晰度**不进指纹** ——
 * 它们不改分镜提示词一个字，拿它们去作废用户手写的词是误伤。
 *
 * ⚠️ 参考图张数**要进指纹**（2026-10-09）：产品图 / 场景图现在会进
 * `buildStoryboardPrompt` 的【参考图用法】段（它们改去喂分镜出图了），
 * 换了图却不作废手写版的话，九宫格会拿旧产品去核对 —— 那正是要防的事。
 *
 * ⚠️ **`shotNotes` 自己不进指纹**（它是被绑定的那一方，不是绑定的依据）。
 */
export function storyboardInputKey(i: VideoInput): string {
  const beats = videoStoryboard(i.duration, i.storyboard)
  return [
    i.productName.trim(),
    i.sellingPoints.trim(),
    i.scene.trim(),
    i.style.trim(),
    String(i.duration),
    i.aspectRatio,
    i.platform ?? '',
    String(i.refProductCount ?? 0),
    String(i.refSceneCount ?? 0),
    /* 模板自带的分镜一并算进来：换模板改的是**镜头结构**（3 段 → 6 镜头），
       不把这几句算进指纹的话，同一产品换模板后手改版仍然"对得上"，又失联了 */
    beats.map((b) => `${b.t}|${b.d}`).join('\n'),
  ].join('\u0001')
}

/**
 * 「让 AI 再写一版分镜提示词」时上送的**上下文**（`POST /api/aigc/prompt/refine` 的 `brief`）。
 *
 * ## 为什么是前端拼、后端不拼
 * 提示词模板的真源就在本文件（`buildStoryboardPrompt`）。后端若自己再拼一遍
 * 【产品】【场景类型】【九宫格结构】……，同一份清单就有两处实现 —— 改一处必漏另一处，
 * 这正是本项目最高的 bug 类型。所以后端只做「把 brief + 当前提示词交给模型、带正文回来」，
 * 上下文一律从这里出。
 *
 * ## 为什么只给事实、不整份重抄原提示词
 * 当前那一版提示词会由后端**另起一段**给模型（【当前这版提示词】），
 * 这里再抄一遍就是同一段文字在一份 prompt 里出现两次 —— 模型会当成两个版本去调和，
 * 输出反而比只给一次更飘。所以这里只交代**模型不可能从原提示词里读出来的东西**：
 * 模板是什么、这支片子的镜头脚本、平台与成片参数。
 *
 * ⚠️ 两条纪律写进了尾部「硬要求」，改的时候别删：
 * ① **保留结构**（九宫格 / 镜号 / 各项硬约束）——否则模型容易「自由发挥」成一段散文，
 *    产出的提示词拿去出图就不再是九宫格；
 * ② **不许编**——上下文里没写的事实一律不能出现在结果里。提示词是原样进生图链路的，
 *    编出来的产品细节会真的被画出来。
 */
/**
 * 「AI 细化镜头」的上下文 —— 骨架本身 + 一段「要你做的」。
 *
 * 骨架**直接复用 `buildStoryboardPrompt`**（与真正要发出去的那份同源）：
 * 模型看到的就是用户看到的那份提示词，其余段落都已定稿，只有【镜头分配】
 * 那几行要写具体。不另拼一份「精简上下文」—— 那样模型改出来的东西该往
 * 哪一段塞就成了猜。
 *
 * 为什么要追加「要你做的」而不是只靠后端 system：**实测模型会数错** ——
 * 它看到「第 1 段「0-2s」→ 第 1-3 格」，按**格数**返回了 9 条（3 段 × 3 格），
 * 于是前端的段数校验直接把它拒掉，用户点了按钮什么都没发生（2026-10-04 真调实测）。
 * 在这份上下文里把「数的是条数、不是格数」说到底，比在后端 system 里说更靠近现场。
 */
export function buildBoardShotsFillBrief(i: VideoInput): string {
  const n = effectiveStoryboard(i.duration, i.storyboard, i.shotNotes).length
  return join([
    buildStoryboardPrompt(i),
    '',
    '【要你做的】',
    '上面【镜头分配】每一段的画面描述还是通用版（或你上一版写的），请把它改写到「照着就能拍」的程度。',
    `· **按条数返回，不是按格数**：上面【镜头分配】现在是 ${n} 条，就返回 ${n} 项；`,
    '  一条可能覆盖多格（如「第 1 段「0-2s」→ 第 1-3 格」），那也**只写一项**，一项覆盖这一段的所有格；',
    '· 每一项**只写画面本身**：不要带「第 1-3 格：」这类前缀（拼回模板时会自动带上），',
    '  也不要复述「第 N 段」或时间段位 —— 骨架里那些一个字都不许动；',
    '· 每一项只写一句、不许换行。',
  ])
}

export function buildStoryboardRefineBrief(i: VideoInput, templateName?: string): string {
  const profile = getPlatform(i.platform)
  const shots = videoStoryboard(i.duration, i.storyboard)
  return join([
    '这是一支电商短视频的拍摄上下文。改写分镜提示词时，下面每一条都必须成立：',
    '',
    templateName ? `【模板】${templateName}` : '',
    '【镜头脚本（这支片子固定的镜头顺序，不要改顺序与段数）】',
    ...shots.map((s) => `· ${s.t}：${s.d}`),
    '',
    `【产品】${i.productName || '（未填写）'}`,
    i.sellingPoints ? `【卖点】${i.sellingPoints}` : '',
    `【${i.sceneLabel ?? '场景类型'}】${i.scene}`,
    `【风格】${i.style}`,
    i.extra.trim() ? `【补充要求】${i.extra.trim()}` : '',
    `【投放平台】${profile.name}（${profile.styleKeywords.join('、')}）`,
    `【成片参数】${i.aspectRatio} · ${i.duration} 秒`,
    '',
    '【改写的硬要求】',
    '· 描述对象是**一张 3×3 九宫格分镜参考图**，不是成片：九格从左上到右下按时间走，每格一个镜头；',
    '· 保留原提示词已有的段落结构（产品 / 场景 / 风格 / 九宫格结构 / 镜头分配 / 技术要求）与全部硬约束，逐条不许丢；',
    '· 镜头描述写到可拍摄：景别、机位、主体动作、光线各是什么，不写「展示产品」这类空话；',
    '· 上面没有给出的事实一律不许编 —— 不加配件、不编功效与认证、不换产品名；',
    '· 输出一份**完整的提示词正文**，不是修改说明。',
  ])
}

/**
 * 「成片提示词」AI 汇总的上下文（`POST /api/aigc/prompt/refine`，`kind='video-prompt'`）。
 *
 * 2026-10-08 用户口径：视频复刻页「应该有个 AI 按钮生成提示词，因为前面参数需要汇总」。
 *
 * ---------------------------------------------------------------------------
 * 与 `buildStoryboardRefineBrief` 的分工：那个改写的是**分镜图提示词**（画九宫格），
 * 这个汇总的是**成片提示词**（给视频模型生成片子）。两者不是一份东西，
 * 所以是两条 brief、两个 system，别合并。
 * ---------------------------------------------------------------------------
 *
 * 为什么是「汇总」而不是「在现有提示词上改写」：
 * `buildVideoPrompt` 拼出来的那一版是**模板产物**（段标题、条件块、逐字有断言的
 * 硬约束全在里面）。让模型在它身上改写，等于把「零文字画面」这类硬约束交给概率 ——
 * 改坏了对着截图看不出来（这正是「AI 写一版」当初被撤掉的原因）。
 * 所以这里**不给它那份模板**，只给参数，让它自己组织一版：
 * 出来的东西**仅供查看与复制**，不参与提交（提交流程仍走 `buildVideoPrompt`）。
 * 两边各是各的真源，谁也不覆盖谁。
 *
 * ⚠️ 参考视频那份分析只收**没过期的**（判据与提示词同一条 `liveVideoAnalysis`，
 * 由调用方算好传进来）—— 把过期结构喂给模型，它会照着**上一条片子**汇总。
 *
 * ---------------------------------------------------------------------------
 * 2026-10-09 改：有分析时，**【镜头脚本】就是那份分析本身**
 * ---------------------------------------------------------------------------
 * 用户口径「第一图的生成 AI 提示词，结构跟脚本要用分析视频的基础去生成」。
 * 上一版把分析列在另一段、【镜头脚本】只写一句「照参考视频执行」，
 * 于是模型汇总出来的【镜头怎么走】是一段散文，与那份「共 N 个镜头」的骨架对不上。
 * 现在骨架直接摆成【镜头脚本】（逐条带镜号），并在【要你做的】里要求**逐镜头写** ——
 * 结构与脚本两件事都从分析出，且只出一次（同一份数据不出现两遍）。
 */
export function buildVideoPromptRefineBrief(i: VideoInput): string {
  const profile = getPlatform(i.platform)
  const shots = effectiveStoryboard(i.duration, i.storyboard, i.shotNotes)
  const nv = i.refVideoCount ?? 0
  const na = nv > 0 ? i.videoAnalysis ?? null : null
  const analysisShots = (na?.shots ?? []).map((s) => s.trim()).filter(Boolean)
  return join([
    '这是一支电商短视频的完整上下文。你的活是把它**汇总成一份成片提示词** ——',
    '也就是直接投给视频生成模型、让它照着生成这支片子的那一段话。',
    '',
    `【片子类型】${nv > 0 ? '视频复刻（照参考视频的镜头结构重拍）' : '带货短视频（自己组织镜头）'}`,
    `【${i.sceneLabel ?? '场景类型'}】${i.scene}`,
    `【产品】${i.productName || '（未填写）'}`,
    i.sellingPoints.trim() ? `【卖点与补充要求】\n${i.sellingPoints.trim()}` : '',
    `【成片参数】${i.duration} 秒 · ${i.aspectRatio} · ${i.style}`,
    profile ? `【投放平台】${profile.name}（${profile.region}）` : '',
    /* 2026-10-09：生成视频只发那张九宫格分镜图（产品图 / 场景图不再上送），
       所以这里只列它 —— 再写「产品图 N 张」会让 AI 去描述一张没发过去的图。 */
    i.refStoryboard
      ? '【已有的参考图】有一张九宫格分镜参考图 —— 它的镜头顺序就是成片要照着的顺序（只定镜头顺序，不要照抄拼贴形式）'
      : '',
    nv > 0 ? `【参考视频】有 ${nv} 条 —— 镜头设计以它为准` : '',
    /*
     * 【镜头脚本】——**有分析时就是那份分析本身**（2026-10-09 改）。
     *
     * 用户口径「第一图的生成 AI 提示词，结构跟脚本要用分析视频的基础去生成」。
     *
     * 上一版把分析另起一段（【参考视频的镜头结构】）列出来，而【镜头脚本】只写
     * 一句「照参考视频执行」—— 于是模型确实拿到了那份清单，但**没有被要求照它写**，
     * 汇总出来的【镜头怎么走】是一段揉在一起的散文（只提了其中几个镜头），
     * 与那份「共 N 个镜头」的骨架对不上。
     *
     * 现在把骨架直接摆成【镜头脚本】，逐条带镜号 —— 它就成了「结构」这一件事的
     * **唯一出处**（同一份数据不出现两次，也就没有「按哪一份写」的分歧）。
     * 【要你做的】里再钉一条「逐镜头写、不许并镜头」，与这里配套。
     */
    ...(na && analysisShots.length
      ? [
          `【镜头脚本 —— 参考视频已逐镜头拆好了，共 ${analysisShots.length} 个，顺序不许动】`,
          na.rhythm.trim() ? `· 整体节奏：${na.rhythm.trim()}` : '',
          na.style.trim() ? `· 画面风格：${na.style.trim()}` : '',
          ...analysisShots.map((s, idx) => `· 镜头 ${idx + 1}：${s}`),
        ].filter(Boolean)
      : nv > 0
        ? [
            '【镜头脚本】本片按参考视频复刻：镜头顺序、机位、景别与切换时机全照参考视频执行，不采用通用分镜模板。',
          ]
        : [`【镜头脚本】\n${shots.map((s) => `· ${s.t}：${s.d}`).join('\n')}`]),
    i.extra.trim() ? `【额外要求】${i.extra.trim()}` : '',
    '',
    '【要你做的】',
    '把上面的信息汇总成一份成片提示词。要求：',
    '· 讲清四件事：**产品是什么**、**这支片子讲什么**（卖点按上面的顺序与条数组织）、',
    '  **镜头怎么走**（景别 / 机位 / 主体动作 / 光线，写到能照着拍）、**成片规格**（时长与比例）；',
    '· 必须写死这两条硬约束，一个字都不许省：',
    '  ① **整片画面里不出现任何可读文字**，包括字幕、标题、贴纸、价格牌、产品机身丝印、包装印刷字与品牌 logo；',
    '     要传达的信息只靠镜头推近、手势指向与构图，文字一律留到后期加；',
    '  ② 产品外观严格按描述还原 —— 不改颜色 / 材质 / 结构，不加描述里没提过的配件；',
    nv > 0
      ? '· 镜头那一段要写明**以参考视频为准** —— 机位、景别、镜头顺序与切换时机照它执行，参考视频里的产品与人物不要照搬；'
      : '',
    /* 有分析骨架时**必须逐镜头写**（用户口径「结构跟脚本要用分析视频的基础去生成」）。
       不钉这一条的话，模型会把它压成一段散文，镜号与镜头数都会丢 ——
       而那正是「成片提示词与参考视频结构对不上」的病根。 */
    na && analysisShots.length
      ? `· 【镜头怎么走】那一段必须**逐镜头写**：上面拆出几个镜头就写几条，每条以「第 N 个镜头」开头，` +
        `顺序、镜头数量与快慢都照那份骨架，**只把每个镜头里拍的东西换成这条片子的产品与画面**；` +
        `不许并镜头、不许增减镜头，也不许退回「产品特写 → 使用过程 → 收尾」这类通用三段式。`
      : '',
    '· 上下文里没有的事实一律不许编：不编功效、认证、数据与促销信息；',
    '· 写成一整段可以直接投给模型的中文提示词，用【】分小段，**不要 Markdown 标题、不要代码块**；',
    '· 长度控制在 400~700 字：太短会漏掉上面那些硬约束，太长模型会抓不住重点。',
    '只输出这段提示词正文。不要开场白、不要解释你是怎么汇总的。',
  ])
}

/**
 * 「全片约束」AI 扩写的上下文（`POST /api/aigc/prompt/refine`，`kind='template-shot'`）。
 *
 * 2026-10-08 用户口径「提示词模板应该是可以编辑的 … 也可以用AI按钮扩写」——
 * 这是「存为模板」弹窗里第二块内容的那颗键。
 *
 * ---------------------------------------------------------------------------
 * 关键差别：这份上下文里**一个镜头画面描述都不放**
 * ---------------------------------------------------------------------------
 * 上游 system 明令「只写跨镜头的约束、不要复述每段拍什么」，但**光靠说**不够 ——
 * 模型只要在上下文里看见逐镜头的画面描述，就会顺手把它们抄进约束里。
 * 而约束最终是拼在提示词**一行**里的，抄进去的镜头内容会与【镜头脚本】段
 * 形成两个真源，以后改一处必漂（本项目栽过很多次的同一类问题）。
 * 所以这里只给「这片子是什么形态」，让它照着写跨镜头的硬要求；
 * 镜头只在**段数**这一层出现一次（用来支撑「不要忽快忽慢」这类节奏约束）。
 *
 * 待改写的那段文字**不走这里** —— 它是 `refinePrompt` 的 `prompt` 字段
 * （用户可能已经在弹窗里改过一版了，那是要在此基础上改写，不是覆盖）。
 */
export function buildTemplateShotRefineBrief(i: VideoInput, templateName?: string): string {
  return join([
    '这是一支电商短视频的参数上下文。要你写的是它的**全片约束** ——',
    '也就是「这条片子从头到尾都不许破的那几条」，不是某个镜头拍什么。',
    '',
    templateName ? `【模板】${templateName}` : '',
    `【产品】${i.productName || '（未填写）'}`,
    i.sellingPoints ? `【卖点】${i.sellingPoints}` : '',
    `【${i.sceneLabel ?? '场景类型'}】${i.scene}`,
    `【风格】${i.style}`,
    `【成片参数】${i.aspectRatio} · ${i.duration} 秒 · ` +
      `${videoStoryboard(i.duration, i.storyboard).length} 个镜头`,
    i.extra.trim() ? `【用户额外补充】${i.extra.trim()}` : '',
    '',
    '【要你做的】',
    '把下面给出的这一版全片约束（可能是空的）改写得更完整、更可执行：',
    '· **只写跨镜头的硬要求**：说清「不许出现什么」「什么必须从头到尾保持一致」；',
    '· **不要复述镜头顺序，也不要写每一段拍什么** —— 那是分镜脚本的活，重复一遍就是两处真源；',
    '· 每一条都要**能验收**，不写「画面要精美」「氛围要高级」这类核对不了的话；',
    '· 上面没有给出的事实一律不许编 —— 不加没提过的道具、不编功效与认证、不换产品名；',
    '· 写成 **3~6 条、一整段、不换行**，用句号或分号连起来。',
  ])
}

export type CopyInput = {
  channel: string
  productName: string
  sellingPoints: string
  audience: string
  /** 平台 id（来自平台档案） */
  platform: string
  tone: string
  length: string
  extra: string
  /**
   * **输出语言**（2026-10-10）。`PRODUCT_LANGUAGES[].id`（BCP-47，如 `en-US`），
   * 或 `'auto'` / 不传 = 跟随平台档案的 `copyLanguage`。
   *
   * 存在的两个理由，其实是同一件事（用户原话「文案这块加一个翻译选项」）：
   *   1. **翻译**：把平台默认语言的稿子改写成另一种语言（投 Ozon 但要一版中文明细）；
   *   2. **兜底**：万一渠道与平台的语言指令还会打架，用户有一个显式的去处能压过它们。
   *
   * 取值**必须**是 `PRODUCT_LANGUAGES` 里的 id 或 `'auto'` —— 语言清单的真源在
   * `config/productAssets.ts`（那份既是商品库的筛选维度，也直接喂上游），
   * 这里不再抄一份，否则「商品库认得的语种」与「文案能选的语言」早晚会漂。
   */
  language?: string
}

/**
 * 解析这一稿**实际要用什么语言**。
 *
 * 优先级（唯一真源，别在别处再判一遍）：
 *   ① 用户显式选了语言（且不是 `'auto'`）→ 用它，并标记 `specified`
 *      （提示词里点明「由用户指定」，让模型知道这是有意的翻译而不是写错）；
 *   ② 否则 → 平台档案的 `copyLanguage`。
 *
 * ⚠️ 它必须**同时**喂给【写作用语】与 `sellingPointBlock` 两处 ——
 * 只改一处的话，提示词里会留下两条互相矛盾的语言指令（就是 FB 出中文那个病的形态）。
 */
export function resolveCopyLanguage(
  language: string | undefined,
  profile: PlatformProfile,
): { label: string; specified: boolean } {
  if (language && language !== 'auto') {
    return { label: productLanguageLabel(language), specified: true }
  }
  return { label: profile.copyLanguage, specified: false }
}

/**
 * 文案提示词。
 *
 * 上一版只把平台当标签打印（`【平台】${i.platform}`），
 * 选小红书和选 Ozon 发给模型的指令逐字相同 —— 所以「国内外差异 / 语言差异 / 卖点差异」
 * 一个都没落地。现在平台档案同时决定：
 *   写作用语、卖点排序与格式、字数硬限制、合规禁词。
 *
 * 语言这一层 2026-10-10 再收一道：**全稿只允许出现一处语言指令**，
 * 由 `resolveCopyLanguage` 统一解析（用户指定 > 平台档案）。以前这里与
 * `sellingPointBlock` 各写一份，渠道与平台打架时就会出现「英文正文」+
 * 「写作用语：中文」两条矛盾指令，模型只能二选一（FB 出中文就是这么来的）。
 */
export function buildCopyPrompt(i: CopyInput): string {
  const profile = getPlatform(i.platform)
  const lang = resolveCopyLanguage(i.language, profile)
  const intent: Record<string, string> = {
    // —— 商品文案 ——
    'selling-point': '提炼产品卖点：按「用户痛点 → 产品事实」成对输出，一条一个购买理由，不写修饰词与形容词堆砌。',
    'detail-copy': '生成详情页文案：按模块顺序出稿（首屏钩子 → 核心卖点 → 参数规格 → 使用场景 → 信任背书），每个模块给标题与正文。',
    'main-keywords': '生成主图卖点关键词：每条 4-8 字的短词，直给不绕弯，可直接排版进主图与卖点图，不做长句。',
    title: '生成电商标题并给出 SEO 诊断（词序、属性词数量、违规词、字符数）。同一份稿同时满足搜索位与点击率。',
    product: '生成商品文案总览：核心卖点清单 + 详情页模块结构，一稿看清全貌。',
    // —— 社媒文案 ——
    xhs: '生成小红书爆款笔记：标题（带情绪钩子）+ 正文（第一人称真实体验）+ 话题标签。',
    moments: '生成朋友圈文案：私域口吻，场景代入 + 真实体验 + 行动号召，不刷屏不硬广。',
    'social-intl':
      '生成 Instagram / Facebook / TikTok 文案：英文正文（首行 125 字符内完成钩子）+ 分平台 hashtag（IG/TikTok 各 8-12 个，混合大词与垂直词）+ 一句可截图的短标语。用母语者写法，禁止中文直译腔与电商式叫卖。',
    // —— 广告文案 ——
    'ad-copy':
      '生成一条完整广告文案，四段一次出齐并分别标注：① 标题（给点击，20 字内）② 正文（给购买理由，场景切入不讲参数堆叠）③ 促销信息（给时限与门槛，必须真实可兑）④ CTA（给一个明确动作，不要「了解更多」这种软话）。四段之间不得重复同一句话。',
    // —— 视频文案 ——
    'live-script':
      '生成直播口播稿：按「开场留人 → 产品演示 → 卖点讲解 → 异议处理 → 逼单」分段，标注时间轴，写出互动话术与承接话术。',
    douyin: '生成带货口播稿：3 秒钩子开头，一口一段（每段 1-2 句，按换气点断句），全程口语，不写书面语与长定语。',
    'short-script':
      '生成短视频脚本与分镜表：逐镜输出表格，每镜标镜号、时长、景别、画面内容、台词 / 字幕、音效或 BGM 提示。拍摄方照着这张表能直接开机，不要写「此处展示产品」这类空话。',
    // —— SEO ——
    seo: '生成 SEO 内容三件套：① 关键词分层（大词 / 精准词 / 长尾词，各给搜索意图说明）② 搜索位可用的产品描述（前 80 字符讲清是什么、给谁用）③ 按目标平台分别给词表（同一个产品在不同平台的搜索词完全不同）。',
    // —— 翻译本地化 ——
    localization:
      '翻译并本地化：不是逐字翻译，而是按目标国家 / 地区的语言习惯重写。必须处理：计量单位（英寸 ↔ 厘米、磅 ↔ 公斤）、尺码体系、货币与价格表达、当地节日与场景引用、称呼与语气。给出译文的同时标出你改动了哪些「直译会出错」的地方。',
  }
  return join([
    intent[i.channel] ?? '生成营销文案。',
    '',
    `【投放平台】${profile.name}（${profile.region}）`,
    `【写作用语】${lang.label} —— 全文必须使用该语言，不要中英夹杂` +
      /* 用户显式选的语言 = 这是在要一版「翻译」，不是写错了 —— 说清楚，别让模型纠结 */
      (lang.specified ? '（本项由用户指定，覆盖平台默认用语）' : ''),
    `· 平台风格：${profile.styleKeywords.join('、')}`,
    `【产品】${i.productName || '（未填写）'}`,
    `【目标人群】${i.audience || '（未填写）'}`,
    `【语气】${i.tone}`,
    `【篇幅】${i.length}`,
    i.extra ? `【补充】${i.extra}` : '',
    '',
    '【卖点组织方式】',
    ...sellingPointBlock(profile, i.sellingPoints, lang.label),
    '',
    '【合规】',
    bannedBlock(profile),
    '· 不编造检测报告、认证、销量数据',
    '· 语言自然，像真人写的，不要 AI 腔',
  ])
}

/* -------------------------------------------------------------------------- */
/* 社交种草图                                                                  */
/* -------------------------------------------------------------------------- */

export type SocialInput = {
  /** SOCIAL_CHANNELS 的 id：xhs | moments */
  channel: string
  productName: string
  sellingPoints: string
  audience: string
  scene: string
  extra: string
  outputs: number
  aspectRatio: string
  resolution: string
  hasRef?: boolean
}

/**
 * 社交种草图提示词（逐张）。
 *
 * 与商品套图的关键差别：**每张的叙事任务不同**（封面拉点击 / 内页讲场景 / 收尾给清单），
 * 所以必须逐张发，且封面单独强调文字占比 —— 封面文字压不下去或提不上来，
 * 直接决定这套图有没有点击率，而这不是「风格关键词」能兜住的。
 *
 * 平台档案在这里只用于风格与禁词；文字占比、叙事结构走 SOCIAL_CHANNELS，
 * 因为微博/小红书那套社媒逻辑和电商主图规范本来就是两套东西。
 */
export function buildSocialPrompts(i: SocialInput): string[] {
  const channel = SOCIAL_CHANNELS.find((c) => c.id === i.channel) ?? SOCIAL_CHANNELS[0]
  const structure = channel.structure
  const n = Math.max(1, i.outputs)

  const header = join([
    `任务：生成 ${channel.name}，共 ${n} 张，第 1 张为封面位。`,
    `说明：${channel.description}`,
    '',
    '【产品信息】',
    `· 品名：${i.productName || '（未填写）'}`,
    `· 核心卖点：${i.sellingPoints || '（未填写，请按产品推断）'}`,
    i.audience ? `· 目标人群：${i.audience}` : '',
    i.scene ? `· 场景倾向：${i.scene}` : '',
    i.extra ? `· 补充要求：${i.extra}` : '',
    ...refFidelityBlock(i.productName, Boolean(i.hasRef)),
    '',
    '【画面文字占比】',
    `· ${channel.textRule}`,
    '',
    '【画面质感】',
    `· ${channel.visualRule}`,
    '',
    '【通用要求】',
    '· 全套图色调、光线、镜头语言统一，像同一次拍摄出的',
    '· 产品外观在全部图片中完全一致，不得变形改色',
    ONE_SHOT_RULE,
    `· 比例 ${i.aspectRatio}，分辨率 ${i.resolution}`,
  ])

  return Array.from({ length: n }, (_, idx) => {
    const base = structure[idx % structure.length]
    const round = Math.floor(idx / structure.length)
    const isCover = idx === 0
    return join([
      header,
      '',
      `【本张唯一要求｜第 ${idx + 1} / ${n} 张${isCover ? '（封面位）' : ''}】`,
      `· 本张只表现：${base}`,
      round > 0 ? `· 与第 ${(idx % structure.length) + 1} 张同属「${base}」，必须换机位与场景，画面上不得雷同` : '',
      isCover
        ? `· 封面是本批唯一的点击入口：主体清晰可辨、画面留白给标题，文字占比严格按「${channel.textRule}」`
        : '· 内页文字克制，画面本身承担主要叙事',
    ])
  })
}

/* -------------------------------------------------------------------------- */
/* 数字人                                                                      */
/* -------------------------------------------------------------------------- */

export type DigitalHumanInput = {
  mode: 'talking-head' | 'clone-avatar'
  /** 口播用途：讲解型 / 带货型。两种意图的稿子结构与节奏完全不同 */
  use?: 'explain' | 'sell'
  avatarSource: 'ai' | 'clone'
  /**
   * 出镜要求（镜头与表情）。2026-10-05 由 `avatarDesc`（形象描述）改语义而来：
   * 形象改成**由一张真实的图提供**（对口型的硬要求），文字描述不再生成脸，
   * 于是这个位置放它本来该做的事 —— 告诉上游怎么拍这个人。
   */
  shotNote: string
  /**
   * 音色 —— **两个字段都要，别只传一个**。
   *
   * `voiceId` 是**真会发给 TTS 的那个字符串**（上游音色 ID，如
   * `Chinese (Mandarin)_Warm_Girl`）；`voiceName` 是接口下发的展示名（「温暖女孩」）。
   * 提示词是给人核对「这一版到底配了什么」的：只写中文名，出问题时对不上上游；
   * 只写 ID，人读不出那是什么声音。
   *
   * ⚠️ 清单真源变了之后（2026-10-06 音色改走后端接口），这里**不许**再去
   * `config/catalog.ts` 查表 —— 那个表已经删了（它只是 skill 那 88 条的子集，
   * 写两份必然漂移）。页面拿到什么就传什么。
   */
  voiceId: string
  voiceName: string
  /** 口播稿，两档都必填（没有稿就没有音频，对口型无从谈起） */
  script: string
  productName: string
  duration: number
  aspectRatio: string
  platform?: string
}

/**
 * 数字人提示词。
 *
 * 两条硬约束来自合规，不是偏好：
 *   1. 克隆真人形象必须声明「已获书面授权」，提示词层面就写死，不给绕过；
 *   2. 口播内容不许出现未验证的功效宣称 —— 数字人是最容易被判定为
 *      「虚假宣传」的形态，因为它看起来像真人在背书。
 */
export type DigitalBoardInput = {
  /** 产品名（可空 —— 这一档不强制有产品） */
  productName?: string
  /** 画面描述：场景 / 动作 / 光线 */
  sceneNote?: string
  aspectRatio?: string
  /** 是否带了产品参考图（决定要不要拼「参考图保真」那一段） */
  hasProductRef?: boolean
  /** 是否带了模特参考图（决定要不要锁人物一致性） */
  hasModelRef?: boolean
}

/**
 * 数字人分镜图 —— 一张「这个人站/坐在这个场景里，正准备开口说话」的画面。
 *
 * ⚠️ 它跟普通的电商场景图**不是一回事**，差的三条都是硬约束，不是风格偏好：
 *
 *   1. **正脸对镜头、嘴部完全可见**。这张图的下一站是对口型接口 ——
 *      侧脸、低头、手挡嘴、麦克风挡嘴、头发遮脸，都会让口型驱动直接崩
 *      （出来的片子嘴不动或整张脸扭曲），而且**上游不会报错**。
 *   2. **单人、上半身为主**。多人同框时模型不知道该驱动哪张脸；
 *      全身远景里脸只有几十个像素，口型细节无从生成。
 *   3. **零文字画面**。同视频链路那条铁律：画面里不许出现任何可读文字
 *      （字幕条 / 标题 / 贴纸 / 包装印刷字 / 机身丝印 / logo / 水印）。
 *      模型对「要字」的指令比「不要字」敏感得多，所以这里一条正向的字面要求都不写。
 */
export function buildDigitalBoardPrompt(i: DigitalBoardInput): string {
  const product = i.productName?.trim()
  return join([
    i.hasModelRef
      ? '任务：以参考图中的人物为主体，生成一张数字人口播视频的**出镜画面**（分镜图）。'
      : '任务：生成一张数字人口播视频的**出镜画面**（分镜图）—— 一个人正准备对着镜头开口说话。',
    '',
    '【人物｜硬性】',
    i.hasModelRef
      ? '· 人物长相、发型、肤色、体型必须与参考图中的人物**完全一致**，不得换脸、不得改年龄感'
      : '· 人物：成年人，单人出镜，面部特征自然可信，不模仿任何真实公众人物的容貌',
    '· **正脸朝向镜头**，五官清晰，眼睛看向镜头',
    '· **嘴部必须完全可见、无任何遮挡**（不能有手、麦克风、头发、围巾、口罩挡住嘴）',
    '· 构图以**上半身**为主（胸像 / 半身），人脸在画面中占足够大 —— 它要拿去逐帧驱动口型',
    '· 表情自然放松，像正在说话的状态（自然的开口或微笑，不做夸张表情）',
    '',
    '【画面】',
    product ? `· 产品：${product}；以自然的方式与人物同框（如手持、置于身前台面），不抢人物主体地位` : '',
    `· 场景与动作：${i.sceneNote?.trim() || '干净简洁的室内环境，柔和的自然光，背景简洁不杂乱'}`,
    '· 镜头固定、平视，画面平稳，不做夸张透视与鱼眼',
    '· 光线均匀柔和，脸部不出现硬阴影或强反差；肤色真实自然',
    '',
    // ⚠️ 必须展开：`join()` 收的是**扁平的一维数组**，把 `string[]` 当一项塞进去
    // 会被 TS 挡下（也真的会把数组拼成 "[object ...]"）
    ...refFidelityBlock(product, i.hasProductRef),
    '',
    '【技术要求】',
    `· 画面比例 ${i.aspectRatio || '9:16'}，竖版构图`,
    '· 质感：真实拍摄的电商口播画面，不是插画、不是 3D 渲染、不是电影感调色',
    '· 不出现面部扭曲、多手指、肢体错位、眼神空洞等生成瑕疵',
    '· **画面里不出现任何文字、字母、数字与符号**（含字幕、标题、贴纸、标签、角标、价格牌、包装印刷字、机身丝印、logo 与水印）；需要后期添加的文字由我方自行排版，你交付干净无字的画面',
  ])
}

export function buildDigitalHumanPrompt(i: DigitalHumanInput): string {
  const mode = DIGITAL_HUMAN_MODES.find((m) => m.id === i.mode) ?? DIGITAL_HUMAN_MODES[0]
  const avatar = AVATAR_SOURCES.find((a) => a.id === i.avatarSource) ?? AVATAR_SOURCES[0]
  const use = DIGITAL_HUMAN_USES.find((u) => u.id === (i.use ?? 'sell')) ?? DIGITAL_HUMAN_USES[1]
  const profile = getPlatform(i.platform)

  /**
   * 讲解型与带货型的分歧写在这里，不靠「口播稿语气」这种模糊描述。
   * 同一段产品信息，讲解型要把它讲成「什么时候用得上」，
   * 带货型要把它讲成「现在不买会亏什么」—— 出片效果差的是这个，不是模型。
   */
  const useRule: Record<string, string[]> = {
    explain: [
      '· 意图：讲解型。观众是来搞明白这个东西的，不是来被推销的',
      '· 结构：先说「这是什么 + 解决什么问题」→ 再讲「怎么用」→ 最后给「适合谁 / 不适合谁」',
      '· 全片不给价格与促销，不做倒计时，不喊「点击下方」；留人靠信息密度',
      '· 允许说缺点与适用边界 —— 讲解型内容的可信度就来自这里，回避反而掉完播',
    ],
    sell: [
      '· 意图：带货型。目标是让人下单，不是让人搞明白',
      '· 结构：前 3 秒抛痛点或结果 → 演示使用 → 价格锚点（原价 / 现价必须真实可兑）→ 明确逼单指令',
      '· 每 5-8 秒一个节奏点（换景 / 换机位 / 出价格），不允许一段长口播拖到底',
      '· 逼单只给一个动作（下单 / 领券 / 进直播间），同时给两个等于没给',
    ],
  }

  return join([
    `任务：生成 ${i.duration} 秒${mode.name}视频。`,
    `说明：${mode.description}`,
    `用途：${use.name} —— ${use.description}`,
    '',
    '【形象】',
    `· 来源：${avatar.name} —— ${avatar.description}`,
    i.avatarSource === 'clone'
      ? '· 已确认：该形象已取得本人书面授权，仅限本店商品内容使用；不得用于其他用途'
      : '· 形象来源：素材库里的 AI 模特图或用户上传的人像照片（作为首帧驱动口型）',
    `· 出镜要求：${i.shotNote.trim() || '固定镜头，正对镜头自然说话，头部小幅摆动'}`,
    '· 人物表情自然、口型与语音同步、眼神看向镜头（或按场景合理偏移）',
    '',
    '【声音】',
    `· 音色：${i.voiceName}（上游音色 ID：${i.voiceId}）`,
    i.use === 'sell'
      ? '· 语速偏快但有停顿，重音落在价格与卖点上；不做夸张叫卖腔'
      : '· 语速平稳偏慢，句间留出思考感；像在跟一个人解释，不是在对一群人演讲',
    '',
    '【口播意图】',
    ...(useRule[use.id] ?? []),
    '',
    i.script.trim() ? '【口播稿｜按此原文，不得增删事实信息】' : '【口播稿｜未提供，请按下列结构生成】',
    i.script.trim() || mode.structure.map((s, idx) => `· ${s}`).join('\n'),
    '',
    '【镜头节奏】',
    ...mode.structure.map((s, idx) => `${idx + 1}. ${s}`),
    '',
    '【技术要求】',
    `· 比例 ${i.aspectRatio}，时长 ${i.duration} 秒`,
    '· 人像清晰稳定，不出现面部扭曲、多手指、口型漂移等生成瑕疵',
    '· 画面不烧字幕，也不出现任何其它文字、字母、数字与符号（含产品机身丝印、包装印刷字、标签、角标、价格牌与 logo）；需要后期添加的文字由我方自行排版，你交付干净无字的画面',
    '',
    '【合规｜红线】',
    `· 投放平台：${profile.name}；图内文字语言 ${profile.textLanguage}`,
    bannedBlock(profile),
    '· 不编造产品功效、检测数据、认证与销量；口播里的每一句产品claim都必须能在产品资料里找到依据',
    i.avatarSource === 'clone'
      ? '· 克隆形象产出的内容，需在发布时按平台要求标注为 AI 生成内容'
      : '· 不模仿任何真实公众人物的容貌与声音',
  ])
}
